/**
 * Evelina — Coffee Story page runtime.
 * Zero dependencies. Deferred. Uses native APIs only.
 *
 * Responsibilities:
 *   1. Tap-to-unmute with persisted state; pause off-screen to save CPU
 *   2. Scroll cue fade on first scroll
 *   3. Chapter reveal via IntersectionObserver
 *   4. Sticky horizontal progress bar (fills with scroll, per-chapter ticks)
 *   5. Mini-player (picture-in-picture):
 *        - On scroll past hero, video flies to bottom-right corner
 *        - FLIP technique: measure start/end rects, animate the delta via
 *          Web Animations API → runs on compositor, butter-smooth
 *        - Tap mini to scroll back to top (smooth), close button to dismiss
 *        - Video keeps playing throughout — same DOM node, never remounted
 *
 * Stays under 4 KB gzipped.
 */

(function () {
  'use strict';

  // EVELINA-FARM-TOUR-V2-GATE: also allow when the Product · Farm 3D tour section is
  // present on a product page (it shares the locationCard logic from
  // this file). Hero-only sub-IIFEs have their own internal guards, so
  // letting the outer IIFE continue here is safe even without .cs-hero.
  const hero       = document.querySelector('.cs-hero');
  const farmTour   = document.querySelector('[data-product-farm-tour]');
  if (!hero && !farmTour) return;

  // ------------------------------------------------------------------
  // Inline CSS fallback. Shopify's server-rendered CSS snippet has
  // been stuck on an ancient revision, so we ship the essential
  // styles for the client-injected bento + product card + location
  // card right here. When the snippet eventually catches up, these
  // rules are simply duplicated (harmless) and the cascade wins on
  // specificity + source order.
  // CS_RUNTIME_CSS is defined at the bottom of this IIFE; we use
  // setTimeout(0) to defer execution past the const initialisation.
  // ------------------------------------------------------------------
  function injectFallbackCSS() {
    if (document.querySelector('style[data-cs-runtime]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-cs-runtime', '1');
    style.textContent = CS_RUNTIME_CSS;
    // Append FIRST so the stale theme snippet can override specific
    // rules if needed; our cascade loses on ties, wins where ours is
    // the only declaration.
    if (document.head.firstChild) {
      document.head.insertBefore(style, document.head.firstChild);
    } else {
      document.head.appendChild(style);
    }
  }

  // ------------------------------------------------------------------
  // 0. Page-open intro — cinematic loading curtain.
  //    Visible while the page settles; lifts up with smooth slide + fade
  //    after the choreographed text reveal finishes (~2.4s).
  //    Session-storage guard: plays once per session, skipped on repeat
  //    views so the animation doesn't become annoying.
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Unlock curtain — orchestrated by CSS, lifecycled by JS.
  //   Plays once per session. Tap to skip. Skipped under
  //   prefers-reduced-motion (the choreography flattens to a
  //   cross-fade so the page is interactive immediately).
  // ------------------------------------------------------------------
  (function unlockCurtain() {
    const unlock = document.querySelector('[data-cs-unlock]');
    if (!unlock) return;

    const reduce = window.matchMedia &&
                   window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let seen = false;
    try { seen = sessionStorage.getItem('cs-unlock-seen') === '1'; } catch (_) {}
    if (seen) {
      unlock.classList.add('is-done');
      return;
    }

    document.body.classList.add('cs-unlock-active');
    try { sessionStorage.setItem('cs-unlock-seen', '1'); } catch (_) {}

    // Timing: synchronised with the CSS keyframes above.
    //   3100 ms — start exit transition
    //   3800 ms — curtain DOM-detached
    const ENTER = reduce ? 600  : 3100;
    const EXIT  = reduce ? 200  : 700;

    window.setTimeout(function () {
      unlock.classList.add('is-exiting');
      document.body.classList.remove('cs-unlock-active');
    }, ENTER);

    window.setTimeout(function () {
      unlock.classList.add('is-done');
      if (unlock.parentNode) unlock.parentNode.removeChild(unlock);
    }, ENTER + EXIT);

    // Tap-to-skip on the curtain.
    unlock.addEventListener('click', function () {
      unlock.classList.add('is-exiting');
      document.body.classList.remove('cs-unlock-active');
      window.setTimeout(function () {
        unlock.classList.add('is-done');
        if (unlock.parentNode) unlock.parentNode.removeChild(unlock);
      }, EXIT);
    }, { once: true });
  })();

  // ------------------------------------------------------------------
  // 0. Connection-aware quality downgrade (Android Chrome, etc)
  //    Shopify emits MP4 sources highest → lowest. On slow connections
  //    we swap the video's active source to the lowest MP4 before it
  //    starts loading — iOS Safari is unaffected (it picks HLS itself
  //    and streams adaptively).
  // ------------------------------------------------------------------
  (function downgradeOnSlowNetwork() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return;
    const type = conn.effectiveType || '';
    const saveData = conn.saveData === true;
    const isSlow = saveData || type === 'slow-2g' || type === '2g' || type === '3g';
    if (!isSlow) return;
    // Pick the lowest-width MP4 from each video's source list
    document.querySelectorAll('.cs-hero__video, .cs-chapter__video').forEach(function (video) {
      const mp4Sources = Array.prototype.slice.call(video.querySelectorAll('source[type="video/mp4"]'));
      if (mp4Sources.length < 2) return;
      // Find smallest width
      let smallest = mp4Sources[0];
      let minW = parseInt(smallest.getAttribute('data-cs-mp4-width') || '99999', 10);
      mp4Sources.forEach(function (s) {
        const w = parseInt(s.getAttribute('data-cs-mp4-width') || '99999', 10);
        if (w < minW) { minW = w; smallest = s; }
      });
      // Remove all other sources (leave HLS in place for browsers that prefer it;
      // keep only the smallest MP4 as the MP4 fallback).
      mp4Sources.forEach(function (s) { if (s !== smallest) s.remove(); });
      // Force the video element to reload with the new source set
      try { video.load(); } catch (e) {}
    });
  })();

  // ------------------------------------------------------------------
  // 1. Hero video: preview loops muted; play button swaps in full video
  //    with sound. Mute button operates on whichever video is active.
  // ------------------------------------------------------------------
  const heroWrap = document.querySelector('[data-cs-video-wrap]');
  const previewVid = heroWrap ? heroWrap.querySelector('[data-cs-preview]') : null;
  const mainVid = heroWrap ? heroWrap.querySelector('[data-cs-main]') : null;
  const playBtn = heroWrap ? heroWrap.querySelector('[data-cs-play]') : null;
  const muteBtn = heroWrap ? heroWrap.querySelector('[data-cs-mute]') : null;

  function currentHeroVideo() {
    if (!heroWrap) return null;
    return heroWrap.classList.contains('is-main-active') ? mainVid : previewVid;
  }

  // Ensure preview starts muted + playing
  if (previewVid) {
    previewVid.muted = true;
    const p = previewVid.play();
    if (p && p.catch) p.catch(function () {});
  }

  // ------------------------------------------------------------------
  // Main hero video — adaptive-bitrate HLS via hls.js on browsers that
  // don't ship native HLS (Chrome / Firefox / Edge). Safari uses its
  // built-in player. Native controls; no custom UI in the way.
  // ------------------------------------------------------------------
  const HLS_JS_URL = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.18/dist/hls.min.js';

  function loadScriptOnce(url) {
    if (loadScriptOnce._cache && loadScriptOnce._cache[url]) {
      return loadScriptOnce._cache[url];
    }
    loadScriptOnce._cache = loadScriptOnce._cache || {};
    const promise = new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.crossOrigin = 'anonymous';
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('script load failed: ' + url)); };
      document.head.appendChild(s);
    });
    loadScriptOnce._cache[url] = promise;
    return promise;
  }

  let mainHlsAttached = false;
  function initMainHls() {
    if (mainHlsAttached || !mainVid) return Promise.resolve();
    // Find the HLS playlist source if Shopify emitted one.
    const hlsSrc = mainVid.querySelector('source[type="application/x-mpegURL"]');
    if (!hlsSrc) return Promise.resolve();   // no HLS, MP4 fallback works

    // Native HLS (Safari, iOS, recent Edge) — nothing to do, the
    // <source> tag is enough.
    if (mainVid.canPlayType('application/vnd.apple.mpegurl')) {
      mainHlsAttached = true;
      return Promise.resolve();
    }

    // Other browsers — bring in hls.js to handle adaptive bitrate.
    return loadScriptOnce(HLS_JS_URL).then(function () {
      if (!window.Hls || !window.Hls.isSupported()) return;
      // Strip MP4 sources so hls.js drives the element exclusively
      // (otherwise Chrome would pre-load the MP4 fallback).
      Array.prototype.slice.call(mainVid.querySelectorAll('source')).forEach(function (s) {
        if (s.type !== 'application/x-mpegURL') s.remove();
      });
      const hls = new window.Hls({
        enableWorker: true,
        capLevelToPlayerSize: true, // never download bigger than display
        startLevel: -1,             // auto-pick by bandwidth
        backBufferLength: 30
      });
      hls.loadSource(hlsSrc.src);
      hls.attachMedia(mainVid);
      mainHlsAttached = true;
      // Recover from network/playback errors gracefully.
      hls.on(window.Hls.Events.ERROR, function (_e, data) {
        if (!data || !data.fatal) return;
        if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else hls.destroy();
      });
    }).catch(function () { /* silent; MP4 fallback if any will play */ });
  }

  // Pre-init HLS during idle time so playback starts instantly when
  // the user hits Play. Don't block the main thread on page load.
  if (mainVid) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initMainHls, { timeout: 2500 });
    } else {
      window.setTimeout(initMainHls, 1500);
    }
  }

  // Play button → swap in main video with native controls.
  if (playBtn && mainVid && heroWrap) {
    playBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      // Make sure HLS is ready (no-op if already attached or native).
      Promise.resolve(initMainHls()).then(function () {
        heroWrap.classList.add('is-main-active');
        mainVid.currentTime = 0;
        mainVid.muted = false;
        mainVid.controls = true;
        mainVid.setAttribute('controls', '');
        mainVid.setAttribute('controlslist', 'nodownload');   // hide download in Chrome
        if (muteBtn) muteBtn.setAttribute('data-muted', 'false');
        const p = mainVid.play();
        if (p && p.catch) p.catch(function () {});
        // Pause preview once the fade is done — save CPU.
        window.setTimeout(function () {
          if (previewVid) previewVid.pause();
        }, 600);
      });
    });
    // When the main video ends, return to the silent preview loop.
    mainVid.addEventListener('ended', function () {
      heroWrap.classList.remove('is-main-active');
      mainVid.currentTime = 0;
      if (previewVid) {
        const p = previewVid.play();
        if (p && p.catch) p.catch(function () {});
      }
    });
  }

  // Mute button — operates on whichever video is active
  if (muteBtn) {
    muteBtn.setAttribute('data-muted', 'true');
    muteBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      const v = currentHeroVideo();
      if (!v) return;
      v.muted = !v.muted;
      muteBtn.setAttribute('data-muted', v.muted ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', v.muted ? 'Unmute video' : 'Mute video');
      if (!v.muted) {
        const p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
    });
  }

  // Off-screen CPU throttling for hero videos
  if ('IntersectionObserver' in window && heroWrap) {
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const v = currentHeroVideo();
        if (!v) return;
        if (entry.isIntersecting) {
          const p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.15 });
    io.observe(heroWrap);
  }

  // ------------------------------------------------------------------
  // 1b. Chapter ambient videos: play only when visible, save battery
  // ------------------------------------------------------------------
  const chapterVideos = document.querySelectorAll('[data-cs-chapter-video]');
  if (chapterVideos.length > 0 && 'IntersectionObserver' in window) {
    const cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const v = entry.target;
        if (entry.isIntersecting) {
          const p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.2 });
    chapterVideos.forEach(function (v) { cio.observe(v); });
  }

  // ------------------------------------------------------------------
  // 2. Scroll cue fade on first scroll
  // EVELINA-FARM-TOUR-V3-HERONULL: outer guard added — on a product page that hosts the
  // Farm 3D tour section, hero is null and unguarded .querySelector
  // here would throw TypeError and halt the entire IIFE before the
  // locationCard logic runs.
  // ------------------------------------------------------------------
  if (hero) {
    const cue = hero.querySelector('.cs-hero__scroll-cue');
    if (cue) {
      const hide = function () {
        if (window.scrollY > 40) {
          cue.classList.add('is-hidden');
          window.removeEventListener('scroll', hide);
        }
      };
      window.addEventListener('scroll', hide, { passive: true });
    }
  }

  // ------------------------------------------------------------------
  // 3. Chapter reveal (IntersectionObserver — smooth fade-in on scroll)
  // ------------------------------------------------------------------
  const chapters = Array.prototype.slice.call(document.querySelectorAll('[data-cs-chapter]'));
  if ('IntersectionObserver' in window && chapters.length > 0) {
    const revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });
    chapters.forEach(function (ch) { revealIO.observe(ch); });
  }

  // ------------------------------------------------------------------
  // 4. Progress CUP — water with spring physics (sloshes on fast scroll).
  //
  //    Two loops:
  //      • Scroll handler sets `targetY` (where the water WANTS to be,
  //        based on viewport-mid scroll position through chapters).
  //      • rAF loop runs a simple spring: `currentY` eases toward
  //        `targetY` with stiffness + damping, so fast scroll lets
  //        the water overshoot and settle like real liquid.
  //
  //    The SVG `transform` attribute is rewritten each frame — no CSS
  //    transitions (they'd mask the spring feel).
  // ------------------------------------------------------------------
  const progress   = document.querySelector('[data-cs-progress]');
  const barEl      = progress ? progress.querySelector('.cs-progress__bar') : null;
  const barFillEl  = document.querySelector('[data-cs-progress-bar-fill]');
  const waterCanvas= document.querySelector('[data-cs-water-canvas]');

  if (progress && barFillEl && waterCanvas && chapters.length > 0) {
    // ======================================================================
    // LIQUID ENGINE — Canvas 2D + 1D wave physics.
    //
    // The surface is a discrete grid of columns. Each column has a height
    // and a vertical velocity. On each frame we apply:
    //   1. SPRING toward the target level (gravity/body force)
    //   2. WAVE PROPAGATION (Laplacian on heights → coupled-oscillator)
    //   3. DAMPING (velocity decay)
    //
    // Impulses are injected into columns when drops land → real ripples
    // that propagate outward from the impact point, interfere with each
    // other, and decay.
    //
    // Rendered with a Canvas 2D path (bottom-anchored, surface traced
    // along all column heights) filled with a vertical gradient.
    // A faint highlight line follows the surface for depth.
    // ======================================================================

    // Cup interior in SVG viewBox (52×42) coordinates.
    // Mug is shifted right by 4 units so its centre (x=26) aligns with
    // the stream line. Interior spans x=16.2 → 35.8.
    const VB_W = 52, VB_H = 42;
    const INT_LEFT  = 16.2;
    const INT_RIGHT = 35.8;
    const INT_TOP   = 10.2;
    const INT_BOT   = 29.6;
    const INT_W = INT_RIGHT - INT_LEFT;
    const INT_H = INT_BOT   - INT_TOP;

    // Wave grid
    const COLS = 56;
    const heights    = new Float32Array(COLS);     // 0..1 (fraction of interior)
    const velocities = new Float32Array(COLS);
    const deltas     = new Float32Array(COLS);

    // Physics tuning
    const RETURN_K   = 0.025;    // how hard columns pull toward targetLevel
    const PROPAGATE  = 0.22;     // how strongly neighbors tug each other
    const COL_DAMPING= 0.992;    // per-frame velocity decay
    const MAX_HEIGHT = 1.08;     // allow tiny overshoot above full
    const MIN_HEIGHT = -0.05;

    // Level spring (drives targetLevel from scroll)
    const LEVEL_STIFFNESS = 0.13;
    const LEVEL_DAMPING   = 0.82;

    // Drop spawn rate limit
    let lastDropTs = 0;
    const DROP_COOLDOWN_MS  = 160;
    const DROP_MIN_VELOCITY = 0.003;

    // State
    let targetLevel     = 0;
    let currentLevel    = 0;
    let levelVelocity   = 0;
    let rafActive       = false;

    // ---- Canvas setup ----
    const ctx = waterCanvas.getContext('2d', { alpha: true });
    let dpr = window.devicePixelRatio || 1;
    let cssW = 0, cssH = 0;

    function resizeCanvas() {
      dpr = window.devicePixelRatio || 1;
      const rect = waterCanvas.getBoundingClientRect();
      cssW = rect.width;
      cssH = rect.height;
      waterCanvas.width  = Math.max(1, Math.floor(cssW * dpr));
      waterCanvas.height = Math.max(1, Math.floor(cssH * dpr));
      // Draw in CSS pixels, then scale once for DPR
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // ---- Impulse API ----
    function impulse(xRatio, amount) {
      // xRatio ∈ [0, 1] along surface. amount negative = push DOWN (drop)
      const col = Math.min(COLS - 1, Math.max(0, Math.floor(xRatio * COLS)));
      velocities[col] += amount;
      // Smooth into immediate neighbors so impulse isn't a sharp spike
      if (col > 0)        velocities[col - 1] += amount * 0.55;
      if (col < COLS - 1) velocities[col + 1] += amount * 0.55;
      if (col > 1)        velocities[col - 2] += amount * 0.22;
      if (col < COLS - 2) velocities[col + 2] += amount * 0.22;
    }

    // ---- Physics step ----
    function physicsStep() {
      // 1. Spring each column toward the target level
      for (let i = 0; i < COLS; i++) {
        const diff = currentLevel - heights[i];
        velocities[i] += diff * RETURN_K;
        velocities[i] *= COL_DAMPING;
      }
      // 2. Wave propagation: each column nudges its neighbors
      for (let i = 0; i < COLS; i++) {
        const left  = heights[i > 0          ? i - 1 : i];
        const right = heights[i < COLS - 1   ? i + 1 : i];
        deltas[i] = (left + right - 2 * heights[i]) * PROPAGATE;
      }
      for (let i = 0; i < COLS; i++) {
        velocities[i] += deltas[i];
      }
      // 3. Integrate — apply velocity to heights, clamp
      for (let i = 0; i < COLS; i++) {
        heights[i] += velocities[i];
        if (heights[i] > MAX_HEIGHT) { heights[i] = MAX_HEIGHT; velocities[i] *= -0.4; }
        if (heights[i] < MIN_HEIGHT) { heights[i] = MIN_HEIGHT; velocities[i] *= -0.4; }
      }
    }

    // ---- Render ----
    function render() {
      if (!cssW || !cssH) return;
      ctx.clearRect(0, 0, cssW, cssH);

      // Cup container's aspect-ratio matches the SVG viewBox (52/42), so
      // the canvas is the SAME size as the rendered SVG — no offsets to
      // compute. Just a 1D scale from viewBox units to CSS px.
      const s = cssW / VB_W;

      const left  = INT_LEFT  * s;
      const right = INT_RIGHT * s;
      const top   = INT_TOP   * s;
      const bot   = INT_BOT   * s;
      const width  = right - left;
      const height = bot - top;

      // Clip path — mirrors the SHIFTED SVG mug-interior path exactly.
      //   SVG: M 17 10 Q 16 10 16 11 L 16 24 Q 16 30 22 30 L 30 30
      //        Q 36 30 36 24 L 36 11 Q 36 10 35 10 Z
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(17 * s, 10 * s);
      ctx.quadraticCurveTo(16 * s, 10 * s, 16 * s, 11 * s);
      ctx.lineTo(          16 * s, 24 * s);
      ctx.quadraticCurveTo(16 * s, 30 * s, 22 * s, 30 * s);
      ctx.lineTo(          30 * s, 30 * s);
      ctx.quadraticCurveTo(36 * s, 30 * s, 36 * s, 24 * s);
      ctx.lineTo(          36 * s, 11 * s);
      ctx.quadraticCurveTo(36 * s, 10 * s, 35 * s, 10 * s);
      ctx.closePath();
      ctx.clip();

      // Build water path
      ctx.beginPath();
      ctx.moveTo(left, bot);
      for (let i = 0; i < COLS; i++) {
        const xRatio = i / (COLS - 1);
        const x = left + xRatio * width;
        const y = bot - Math.max(0, heights[i]) * height;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(right, bot);
      ctx.closePath();

      // Gradient fill
      const grad = ctx.createLinearGradient(0, top, 0, bot);
      grad.addColorStop(0,   '#FFFFFF');
      grad.addColorStop(0.7, 'rgba(255,255,255,0.97)');
      grad.addColorStop(1,   'rgba(232,225,213,0.92)');
      ctx.fillStyle = grad;
      ctx.fill();

      // Surface highlight — a faint stroke along the top edge
      ctx.beginPath();
      for (let i = 0; i < COLS; i++) {
        const xRatio = i / (COLS - 1);
        const x = left + xRatio * width;
        const y = bot - Math.max(0, heights[i]) * height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 0.7;
      ctx.stroke();

      ctx.restore();
    }

    // ---- Drop spawning (visible drops from bar) ----
    function spawnDrop() {
      const d = document.createElement('span');
      d.className = 'cs-progress__drop';
      const jitter = (Math.random() - 0.5) * 2;
      d.style.marginLeft = jitter.toFixed(1) + 'px';
      barEl.appendChild(d);
      d.addEventListener('animationend', function () {
        if (d.parentNode) d.parentNode.removeChild(d);
        // Drop "lands" → inject impulse into water. xRatio ~ center with jitter.
        const xr = 0.5 + jitter * 0.1;
        impulse(xr, -0.06);
      }, { once: true });
    }

    // ---- Main frame loop ----
    function loop() {
      // Level spring
      const diff = targetLevel - currentLevel;
      levelVelocity += diff * LEVEL_STIFFNESS;
      levelVelocity *= LEVEL_DAMPING;
      currentLevel += levelVelocity;
      if (currentLevel > 1.03) currentLevel = 1.03;
      if (currentLevel < -0.03) currentLevel = -0.03;
      const visLevel = Math.min(1, Math.max(0, currentLevel));

      // Bar drain
      barFillEl.style.top = (visLevel * 100).toFixed(2) + '%';

      // Water physics + render
      physicsStep();
      render();

      // Drip drops when level is flowing
      const ts = performance.now();
      if (levelVelocity > DROP_MIN_VELOCITY && ts - lastDropTs > DROP_COOLDOWN_MS && barEl) {
        spawnDrop();
        lastDropTs = ts;
      }

      window.requestAnimationFrame(loop);
    }

    // ---- Scroll → target level + visibility ----
    //
    // Visibility:
    //   Appears as soon as the visitor starts scrolling (scroll > 40 px).
    //   Hides the moment the FOOTER enters the viewport — so the rail
    //   stays on screen through the CTA section, then elegantly exits
    //   when the site footer begins rising into view.
    //
    // Level:
    //   Maps scroll position linearly across ALL chapter sections
    //   (first chapter top → last chapter bottom). 100 % at end of last
    //   chapter, then clamped. Works for any chapter count.
    // ------------------------------------------------------------------
    const SHOW_SCROLL_THRESHOLD = 40;
    let cachedFooter = null;
    function getFooter() {
      // Cached lookup — footer doesn't move.
      if (cachedFooter && document.body.contains(cachedFooter)) return cachedFooter;
      cachedFooter =
        document.querySelector('footer') ||
        document.querySelector('.site-footer, .footer-wrapper') ||
        document.querySelector('[class*="shopify-section-group-footer-group"]');
      return cachedFooter;
    }

    function computeTarget() {
      const y  = window.scrollY || window.pageYOffset;
      const vh = window.innerHeight;
      const first = chapters[0];
      const last  = chapters[chapters.length - 1];
      if (!first || !last) return;
      const rangeStart = first.offsetTop;
      const rangeEnd   = last.offsetTop + last.offsetHeight;
      const range      = Math.max(1, rangeEnd - rangeStart);

      const viewportMid = y + vh * 0.5;
      let pct = (viewportMid - rangeStart) / range;
      if (pct < 0) pct = 0;
      if (pct > 1) pct = 1;
      targetLevel = pct;

      // Hide when the footer begins entering the viewport. Using live
      // getBoundingClientRect so it works regardless of layout shifts.
      const footer = getFooter();
      let footerInView = false;
      if (footer) {
        const rect = footer.getBoundingClientRect();
        // If the top of the footer is below the viewport bottom, it's
        // not yet in view. Once it crosses into the viewport, hide.
        footerInView = rect.top < vh * 0.9;
      }

      const visible = y > SHOW_SCROLL_THRESHOLD && !footerInView;
      if (visible) progress.classList.add('is-visible');
      else         progress.classList.remove('is-visible');
    }

    let scrollScheduled = false;
    function onScroll() {
      if (scrollScheduled) return;
      scrollScheduled = true;
      window.requestAnimationFrame(function () {
        computeTarget();
        scrollScheduled = false;
      });
    }

    // Initial setup
    resizeCanvas();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { resizeCanvas(); onScroll(); }, { passive: true });
    computeTarget();
    window.requestAnimationFrame(loop);
  }

  // ------------------------------------------------------------------
  // 5. Floating mini preview — REMOVED.
  //   The previous picture-in-picture mini caused too many
  //   compatibility quirks across Safari / iOS / inline-cache
  //   variants. Hero video is the single source of truth now.
  // ------------------------------------------------------------------

  // ------------------------------------------------------------------
  // 6. Pause all videos when tab hidden (battery saver)
  // ------------------------------------------------------------------
  document.addEventListener('visibilitychange', function () {
    const v = currentHeroVideo();
    if (!v) return;
    if (document.hidden) {
      v.pause();
    } else {
      const p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
  });

  // ==================================================================
  // 7. LOCATION CARD — interactive map + live time + live weather.
  //
  //    Lives at the top of chapter 1 (Místo). The "cockpit" behaviour:
  //      a) Local clock ticks every second in the farm's IANA timezone
  //      b) Current weather fetched from Open-Meteo (no API key); the
  //         icon + description + temperature update every 10 minutes
  //      c) Leaflet map with CARTO Dark Matter tiles lazy-loads when
  //         the card approaches the viewport (IntersectionObserver,
  //         200 px rootMargin). Before that, a fallback gradient +
  //         CSS pin sit in place so the card never looks empty.
  //
  //    All of it is progressive: the static data in the Liquid markup
  //    (place name, coords, altitude) survives JS/network failure.
  // ==================================================================
  (function locationCard() {
    // STEP A — client-side "bento" rebuild for chapter 1.
    //
    // Shopify's server-render cache has been stubbornly stuck on an
    // old template state, so we compose the chapter 1 experience from
    // the browser: a single bento grid that fuses the live map, the
    // chapter's own text + video, and the three stats tiles into one
    // organised composition. If the server ever renders an equivalent
    // [data-cs-bento] node, we bail and let it win.
    buildBentoIfMissing();

    const cards = document.querySelectorAll('[data-cs-location]');
    if (!cards.length) return;

    function buildBentoIfMissing() {
      if (document.querySelector('[data-cs-bento]')) return;

      // Farm centroid — computed from the GeoJSON polygon of Finca
      // La Eneida's actual boundary (see FINCA_BOUNDARY below). The
      // broader "Palmor" name is kept for the region label; the
      // coordinates we display reflect the real farm location.
      const loc = {
        lat:        '10.8004',
        lng:        '-74.0097',
        zoom:       '14',
        place_name: 'Finca La Eneida · Palmor, Sierra Nevada',
        altitude:   '1 500+ m',
        timezone:   'America/Bogota',
        map_link:   'https://www.google.com/maps/@10.8004,-74.0097,14z'
      };

      const chapters = document.querySelectorAll('[data-cs-chapter]');
      if (!chapters.length) return;
      const ch1 = chapters[0];

      const content = {
        number:  '01',
        title:   'Místo',
        heading: 'Finca La Eneida, Palmor — 1 300 až 2 000 m n. m.',
        // Sub-heading — sits between the live strip and the body copy.
        // Larger and more prominent than the body, smaller than the
        // section heading; it reads as the "lead" sentence of the
        // chapter.
        subheading:
          'Tady káva dozrává pomaleji než většina na světě.\n' +
          'Chlad z hor a teplý vzduch od Karibiku zpomalují zrání plodů.',
        body:
          'Díky tomu mají zrna víc času vytvořit cukry a komplexitu.\n' +
          'Proto chutná sladší, čistší a vyváženěji.'
      };

      const mediaNode = ch1.querySelector('.cs-chapter__media');

      // EDITORIAL LAYOUT — 3 rows:
      //   Row 1:  Header (01 · Místo + headline)
      //   Row 2:  Full-width map (square on mobile, landscape on desktop)
      //   Row 3:  2-col grid below the map
      //             Left:  video + body copy + place badge
      //             Right: stats (time / weather / wind / altitude) + map link
      const bento = document.createElement('section');
      bento.className = 'cs-bento cs-root';
      bento.setAttribute('data-cs-bento', '');
      bento.setAttribute('data-cs-chapter', '');
      bento.setAttribute('data-cs-chapter-number', content.number);
      bento.setAttribute('data-cs-chapter-title', content.title);

      bento.innerHTML =
        // ---- Row 1: Full-width header ----
        '<header class="cs-bento__header">' +
          '<div class="cs-bento__meta">' +
            '<span class="cs-bento__num">' + esc(content.number) + '</span>' +
            '<span class="cs-bento__meta-dot" aria-hidden="true"></span>' +
            '<span class="cs-bento__kicker">' + esc(content.title) + '</span>' +
          '</div>' +
          '<h2 class="cs-bento__heading">' + esc(content.heading) + '</h2>' +
        '</header>' +

        // ---- Row 2: Full-width map ----
        '<div class="cs-bento__map-wrap">' +
          locationCardMarkup(loc) +
        '</div>' +

        // ---- Row 3: video (1:1 like chapter videos) | side column ----
        // The live-info strip now lives in the side column, ABOVE the
        // description body. Dense grid layout — no wasted whitespace.
        '<div class="cs-bento__media-row">' +
          '<div class="cs-bento__video" data-cs-bento-media></div>' +
          '<div class="cs-bento__side">' +
            // Live data strip:
            //   ●LIVE  | čas  | počasí  | vítr  | výška  →  Otevřít v mapě
            // Live pill is non-interactive; it's a "this data is real"
            // signal with a green pulsing dot + radiating ring. Stats
            // sit on hairline-separated columns. Map link is the only
            // CTA, gold-accent pill on the right.
            '<div class="cs-bento__strip">' +
              '<div class="cs-bento__live" aria-label="Živé údaje">' +
                '<span class="cs-bento__live-dot" aria-hidden="true"></span>' +
                '<span class="cs-bento__live-label">Live</span>' +
              '</div>' +
              '<dl class="cs-bento__strip-stats">' +
                '<div class="cs-bento__strip-stat">' +
                  '<dt>Čas</dt>' +
                  '<dd data-cs-location-time>—</dd>' +
                '</div>' +
                '<div class="cs-bento__strip-stat">' +
                  '<dt>Počasí</dt>' +
                  '<dd data-cs-location-weather>—</dd>' +
                '</div>' +
                '<div class="cs-bento__strip-stat">' +
                  '<dt>Vítr</dt>' +
                  '<dd data-cs-location-wind>—</dd>' +
                '</div>' +
                '<div class="cs-bento__strip-stat">' +
                  '<dt>Výška</dt>' +
                  '<dd>' + esc(loc.altitude) + '</dd>' +
                '</div>' +
              '</dl>' +
              '<a class="cs-bento__strip-link" href="' + esc(loc.map_link) + '" target="_blank" rel="noopener noreferrer">' +
                '<span>Otevřít v mapě</span>' +
                '<svg viewBox="0 0 14 14" width="10" height="10" aria-hidden="true">' +
                  '<path d="M3 11 L11 3 M5 3 L11 3 L11 9" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</a>' +
            '</div>' +
            // Lead sub-heading sits between the strip and the body —
            // larger and softer than the body so it reads as the
            // chapter's "tone-setter".
            (content.subheading
              ? '<p class="cs-bento__subheading">' + formatBody(content.subheading).replace(/^<p>|<\/p>$/g, '') + '</p>'
              : '') +
            '<div class="cs-bento__body">' + formatBody(content.body) + '</div>' +
          '</div>' +
        '</div>';

      // Wire up the map location data attributes
      const aside = bento.querySelector('.cs-location');
      if (aside) {
        aside.setAttribute('data-cs-location', '');
        aside.setAttribute('data-lat',   loc.lat);
        aside.setAttribute('data-lng',   loc.lng);
        aside.setAttribute('data-tz',    loc.timezone);
        aside.setAttribute('data-zoom',  loc.zoom);
        aside.setAttribute('data-place', loc.place_name);

        // Runtime override — the base `.cs-location` rule has
        // `max-width: 1040px` and switches to `display: grid;
        // grid-template-columns: 1.5fr 1fr` at ≥1020 px (for the
        // standalone card's map + body layout). In the bento variant
        // the body is hidden, so BOTH the max-width AND the grid
        // leave an empty strip on the right. Inline styles beat every
        // CSS rule regardless of which snippet version is cached.
        aside.style.display = 'block';
        aside.style.gridTemplateColumns = 'none';
        aside.style.maxWidth = 'none';
        aside.style.margin = '0';
        aside.style.width = '100%';
        aside.style.height = '100%';
        aside.style.position = 'absolute';
        aside.style.inset = '0';

        const mapNode = aside.querySelector('.cs-location__map');
        if (mapNode) {
          mapNode.style.width = '100%';
          mapNode.style.height = '100%';
          mapNode.style.aspectRatio = 'auto';
          mapNode.style.minHeight = '0';
          mapNode.style.borderRight = '0';
        }
      }

      // MOVE the chapter's media into the video row (keeps video state)
      const videoSlot = bento.querySelector('[data-cs-bento-media]');
      if (videoSlot && mediaNode) {
        videoSlot.appendChild(mediaNode);
      }

      // ── Map loader overlay ──
      // Three phases tuned to actual map loading behaviour:
      //
      //   PHASE 1 (0 → 88 %, ~5 s, ease-out cubic)
      //     Steady progress while the API + WebGL + first batch of
      //     tiles are downloading. Holds at 88 if map isn't ready yet.
      //
      //   PHASE 2 (hold at 88 → 92 %, +1.4 s buffer after `.is-loaded`)
      //     `is-loaded` fires when the camera reports steady, but the
      //     actual GPU tile texture upload + paint takes ~1 s longer.
      //     We hold + tick slowly during this window so the bar isn't
      //     suspiciously frozen at 88 while tiles settle.
      //
      //   PHASE 3 (92 → 100 % over 500 ms, then fade)
      //     Smooth finish, then the overlay scales out — by which
      //     point the underlying map has actually painted.
      //
      // 12 s hard ceiling catches genuinely-stuck cases.
      (function startMapLoader() {
        const loader = bento.querySelector('[data-cs-map-loader]');
        const barEl  = bento.querySelector('[data-cs-map-loader-bar]');
        const pctEl  = bento.querySelector('[data-cs-map-loader-pct]');
        if (!loader || !barEl || !pctEl) return;

        const PHASE1_MS  = 5000;
        const PHASE1_MAX = 88;
        const BUFFER_MS  = 1400;
        const PHASE2_MAX = 92;
        const FINISH_MS  = 500;

        let phase = 1;
        let pctCurrent = 0;
        let raf = null;
        let done = false;
        let readyAt = 0;
        const start = performance.now();

        function setPct(pct) {
          pctCurrent = pct;
          barEl.style.width = pct + '%';
          pctEl.textContent = Math.round(pct) + ' %';
        }
        function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

        function tick(now) {
          if (done) return;
          if (phase === 1) {
            const t = Math.min(1, (now - start) / PHASE1_MS);
            setPct(easeOutCubic(t) * PHASE1_MAX);
            if (t >= 1) phase = 2;   // hold until ready signal
          } else if (phase === 2) {
            // Slow drift 88 → 92 while we wait for tiles to paint
            // after the camera reported steady.
            if (readyAt) {
              const t = Math.min(1, (now - readyAt) / BUFFER_MS);
              setPct(PHASE1_MAX + easeOutCubic(t) * (PHASE2_MAX - PHASE1_MAX));
              if (t >= 1) {
                phase = 3;
                finishStart = now;
                pctAtFinishStart = pctCurrent;
              }
            }
          } else if (phase === 3) {
            const t = Math.min(1, (now - finishStart) / FINISH_MS);
            setPct(pctAtFinishStart + easeOutCubic(t) * (100 - pctAtFinishStart));
            if (t >= 1) {
              hideLoader();
              return;
            }
          }
          raf = requestAnimationFrame(tick);
        }
        let finishStart = 0;
        let pctAtFinishStart = 0;

        function hideLoader() {
          if (done) return;
          done = true;
          if (raf) cancelAnimationFrame(raf);
          setPct(100);
          loader.classList.add('is-hidden');
          setTimeout(function () {
            if (loader.parentNode) loader.parentNode.removeChild(loader);
          }, 700);
        }

        raf = requestAnimationFrame(tick);

        // Watch for `.is-loaded` on the wrap → start the buffer window
        // (1.4 s) before transitioning to the 100 % phase. This buffer
        // is the key — it gives the GPU time to paint actual tiles
        // after the camera reports steady, so the overlay doesn't
        // disappear onto an empty map.
        const mapWrapEl = bento.querySelector('[data-cs-location-map-wrap]');
        if (mapWrapEl) {
          const obs = new MutationObserver(function () {
            if (mapWrapEl.classList.contains('is-loaded') && !readyAt) {
              readyAt = performance.now();
              if (phase === 1) phase = 2;   // jump out of the hold-at-88 state
              obs.disconnect();
            }
          });
          obs.observe(mapWrapEl, { attributes: true, attributeFilter: ['class'] });
        }

        // Hard ceiling — even if the map never signals ready (slow 3G,
        // 3D not supported, blocked tiles, etc.), kill the loader at
        // 12 s so the page is never stuck looking like it's loading.
        setTimeout(hideLoader, 12000);
      })();

      // Play overlay — only fires when user wants to RESUME a paused
      // animation. The strip has no pause/play button (we removed it),
      // but the central play button on the map serves as a recovery
      // mechanism if the loop ever stops (visibilitychange, init
      // failure, etc.).
      const replayBtn = bento.querySelector('[data-cs-map-replay]');
      const mapWrap   = bento.querySelector('[data-cs-location-map-wrap]');
      if (replayBtn) {
        replayBtn.addEventListener('click', function () {
          document.dispatchEvent(new CustomEvent('cs:map-resume'));
        });
      }
      // Mirror the loop's running state to a CSS class on the map-wrap.
      // CSS uses .is-paused to fade the play overlay in.
      document.addEventListener('cs:map-loop-state', function (e) {
        const paused = !(e && e.detail && e.detail.looping);
        if (mapWrap) mapWrap.classList.toggle('is-paused', paused);
      });

      ch1.parentNode.insertBefore(bento, ch1);
      ch1.setAttribute('hidden', '');
      ch1.style.display = 'none';
      ch1.removeAttribute('data-cs-chapter');

      const revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            bento.classList.add('is-visible');
            revealIO.disconnect();
          }
        });
      }, { threshold: 0.08 });
      revealIO.observe(bento);
    }

    function locationCardMarkup(data) {
      return (
        '<aside class="cs-location cs-location--in-bento" data-cs-location' +
          ' aria-label="' + esc(data.place_name) + '">' +
          '<div class="cs-location__map" data-cs-location-map-wrap>' +
            '<div class="cs-location__map-fallback" aria-hidden="true">' +
              '<div class="cs-location__map-grid"></div>' +
              '<div class="cs-location__map-glow"></div>' +
            '</div>' +
            // Beautiful loading overlay — shown until the 3D tiles
            // settle. Hides the raw pixelated tile-loading view.
            '<div class="cs-map-loader" data-cs-map-loader aria-hidden="true">' +
              '<div class="cs-map-loader__inner">' +
                '<div class="cs-map-loader__eyebrow">' +
                  '<span class="cs-map-loader__eyebrow-dot" aria-hidden="true"></span>' +
                  '<span>Satelitní data</span>' +
                '</div>' +
                '<div class="cs-map-loader__title">Sierra Nevada · Kolumbie</div>' +
                '<div class="cs-map-loader__bar" aria-hidden="true">' +
                  '<div class="cs-map-loader__bar-track"></div>' +
                  '<div class="cs-map-loader__bar-fill" data-cs-map-loader-bar></div>' +
                '</div>' +
                '<div class="cs-map-loader__meta">' +
                  '<span class="cs-map-loader__pct" data-cs-map-loader-pct>0 %</span>' +
                  '<span class="cs-map-loader__hint">Načítání 3D terénu…</span>' +
                '</div>' +
              '</div>' +
            '</div>' +
            '<div class="cs-location__map-inner" data-cs-location-map></div>' +
            '<div class="cs-location__map-vignette" aria-hidden="true"></div>' +
            // Transparent event-blocker overlay — see comments at the
            // shield-listener wire-up site below.
            '<div class="cs-location__shield" data-cs-map-shield aria-hidden="true"></div>' +
            '<div class="cs-location__pin cs-location__pin--fallback" aria-hidden="true">' +
              '<span class="cs-location__pin-pulse"></span>' +
              '<span class="cs-location__pin-ring"></span>' +
              '<span class="cs-location__pin-dot"></span>' +
            '</div>' +
            // Play overlay — only visible when the camera animation is
            // paused (e.g. tab was hidden, or 3D init failed). Click
            // resumes the loop. Visibility is driven by `.is-paused`
            // on the map-wrap, toggled in JS via `cs:map-loop-state`.
            '<button type="button" class="cs-location__replay" data-cs-map-replay aria-label="Spustit animaci">' +
              '<span class="cs-location__replay-circle" aria-hidden="true">' +
                '<svg viewBox="0 0 24 24" width="22" height="22">' +
                  '<path d="M8 5v14l11-7z" fill="currentColor"/>' +
                '</svg>' +
              '</span>' +
            '</button>' +
          '</div>' +
        '</aside>'
      );
    }

    function formatBody(text) {
      // Render double-newline as paragraph break, single as <br>
      return String(text)
        .split(/\n\n+/)
        .map(function (p) {
          return '<p>' + esc(p).replace(/\n/g, '<br>') + '</p>';
        })
        .join('');
    }

    function esc(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
      });
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

    // WMO weather codes → Czech description + compact SVG icon key.
    // Source: https://open-meteo.com/en/docs  §"Weather variable documentation"
    const WX = {
      0:  ['jasno',              'sun'],
      1:  ['převážně jasno',     'sun'],
      2:  ['polojasno',          'cloud-sun'],
      3:  ['zataženo',           'cloud'],
      45: ['mlha',               'fog'],
      48: ['mlha',               'fog'],
      51: ['mrholení',           'rain'],
      53: ['mrholení',           'rain'],
      55: ['mrholení',           'rain'],
      56: ['mrznoucí mrholení',  'rain'],
      57: ['mrznoucí mrholení',  'rain'],
      61: ['slabý déšť',         'rain'],
      63: ['déšť',               'rain'],
      65: ['silný déšť',         'rain'],
      66: ['mrznoucí déšť',      'rain'],
      67: ['mrznoucí déšť',      'rain'],
      71: ['slabé sněžení',      'snow'],
      73: ['sněžení',            'snow'],
      75: ['silné sněžení',      'snow'],
      77: ['sněhová zrna',       'snow'],
      80: ['přeháňky',           'rain'],
      81: ['přeháňky',           'rain'],
      82: ['silné přeháňky',     'rain'],
      85: ['sněhové přeháňky',   'snow'],
      86: ['sněhové přeháňky',   'snow'],
      95: ['bouřka',             'storm'],
      96: ['bouřka s kroupami',  'storm'],
      99: ['silná bouřka',       'storm']
    };

    // Inline SVG icons (stroke = currentColor so we inherit card color).
    const ICONS = {
      'sun':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
        '<g stroke="currentColor" stroke-width="1.5" stroke-linecap="round">' +
        '<line x1="12" y1="2.5"  x2="12" y2="5"/>' +
        '<line x1="12" y1="19"   x2="12" y2="21.5"/>' +
        '<line x1="2.5"  y1="12" x2="5"  y2="12"/>' +
        '<line x1="19"   y1="12" x2="21.5" y2="12"/>' +
        '<line x1="5"    y1="5"  x2="6.8" y2="6.8"/>' +
        '<line x1="17.2" y1="17.2" x2="19" y2="19"/>' +
        '<line x1="5"    y1="19" x2="6.8" y2="17.2"/>' +
        '<line x1="17.2" y1="6.8" x2="19" y2="5"/>' +
        '</g></svg>',
      'cloud-sun':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<circle cx="8" cy="8" r="2.6" fill="none" stroke="currentColor" stroke-width="1.3"/>' +
        '<g stroke="currentColor" stroke-width="1.2" stroke-linecap="round" opacity="0.75">' +
        '<line x1="8"   y1="2"   x2="8"   y2="3.6"/>' +
        '<line x1="2"   y1="8"   x2="3.6" y2="8"/>' +
        '<line x1="3.8" y1="3.8" x2="5"   y2="5"/>' +
        '<line x1="12.4" y1="3.8" x2="11.2" y2="5"/>' +
        '</g>' +
        '<path d="M8 17 Q5 17 5 14.5 Q5 12 7.5 12 Q8.5 10 10.5 10 Q13 10 13.5 12.2 Q16 12.2 16 14.5 Q16 17 13 17 Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
        '</svg>',
      'cloud':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M7 18 Q3 18 3 14 Q3 10 7 10 Q8 7 12 7 Q16 7 17 11 Q21 11 21 14.5 Q21 18 17 18 Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
        '</svg>',
      'fog':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<g stroke="currentColor" stroke-width="1.5" stroke-linecap="round">' +
        '<line x1="3" y1="8"  x2="21" y2="8"/>' +
        '<line x1="3" y1="12" x2="21" y2="12"/>' +
        '<line x1="5" y1="16" x2="19" y2="16"/>' +
        '<line x1="7" y1="20" x2="17" y2="20"/>' +
        '</g></svg>',
      'rain':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M7 14 Q3 14 3 10.5 Q3 7 7 7 Q8 5 11 5 Q15 5 15.5 8 Q20 8 20 11.5 Q20 14 16 14 Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<g stroke="currentColor" stroke-width="1.3" stroke-linecap="round">' +
        '<line x1="8"  y1="17" x2="7"  y2="21"/>' +
        '<line x1="12" y1="17" x2="11" y2="21"/>' +
        '<line x1="16" y1="17" x2="15" y2="21"/>' +
        '</g></svg>',
      'snow':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M7 14 Q3 14 3 10.5 Q3 7 7 7 Q8 5 11 5 Q15 5 15.5 8 Q20 8 20 11.5 Q20 14 16 14 Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<g fill="currentColor">' +
        '<circle cx="8"  cy="19" r="1"/>' +
        '<circle cx="12" cy="20" r="1"/>' +
        '<circle cx="16" cy="19" r="1"/>' +
        '</g></svg>',
      'storm':
        '<svg class="cs-location__weather-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M7 13 Q3 13 3 9.5 Q3 6 7 6 Q8 4 11 4 Q15 4 15.5 7 Q20 7 20 10.5 Q20 13 16 13 Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<path d="M12 14 L9 19 L12 19 L10 23" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>' +
        '</svg>'
    };

    cards.forEach(initCard);

    function initCard(card) {
      const lat = parseFloat(card.dataset.lat);
      const lng = parseFloat(card.dataset.lng);
      const tz  = card.dataset.tz || 'UTC';
      const zoom = parseInt(card.dataset.zoom, 10) || 10;

      if (!isFinite(lat) || !isFinite(lng)) return;

      startClock(card, tz);
      startWeather(card, lat, lng);

      // Lazy-load map when the card nears the viewport
      const mapWrap = card.querySelector('[data-cs-location-map]');
      if (!mapWrap) return;

      // Reveal card on enter
      const revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            card.classList.add('is-visible');
            revealIO.disconnect();
          }
        });
      }, { threshold: 0.12 });
      revealIO.observe(card);

      // Lazy-load whichever map strategy we're using. Try 3D first if
      // a Google Maps key is available; fall back to Leaflet OSM.
      const loadIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            loadIO.disconnect();
            const apiKey = getGoogleMapsKey();
            if (apiKey) {
              build3DMap(card, mapWrap, lat, lng, apiKey).catch(function (err) {
                // 3D failed (bad key, tiles API not enabled, billing off,
                // referrer restriction, whatever). Fall back to 2D so
                // the page still works.
                if (window.console) console.warn('[coffee-story] 3D map failed, using 2D fallback:', err);
                ensureLeaflet(function () { buildMap(card, mapWrap, lat, lng, zoom); });
              });
            } else {
              ensureLeaflet(function () { buildMap(card, mapWrap, lat, lng, zoom); });
            }
          }
        });
      }, { rootMargin: '300px' });
      loadIO.observe(card);
    }

    // ------------------------------------------------------------------
    // GOOGLE MAPS 3D — Photorealistic Tiles via <gmp-map-3d>.
    //
    // Uses Google's newest Maps JS API features:
    //   • Map3DElement — photorealistic 3D tiles rendered from Google
    //     Earth's vector + imagery data (mountains, buildings, foliage)
    //   • Marker3DInteractiveElement — 3D marker anchored to terrain
    //   • flyCameraAround() — cinematic orbit around a target
    //   • flyCameraTo() — smooth animated transitions (zoom / recenter)
    //
    // Requires a Google Cloud project with:
    //   • Billing enabled (mandatory for photorealistic 3D tiles)
    //   • "Maps JavaScript API" enabled
    //   • "Map Tiles API" enabled
    //   • API key restricted to this site's HTTP referrers
    //
    // Cost: $50 per 1000 sessions after Google's $200/mo free credit.
    // ------------------------------------------------------------------
    function getGoogleMapsKey() {
      // 1. Meta tag rendered from theme setting
      const meta = document.querySelector('meta[name="cs-google-maps-key"]');
      if (meta && meta.content && meta.content.trim()) return meta.content.trim();
      // 2. Hardcoded fallback (empty by default)
      return GMAPS_KEY_FALLBACK || null;
    }

    // ------------------------------------------------------------------
    // Finca La Eneida farm boundary (GeoJSON from geojson.io drawing).
    // GeoJSON stores coords as [lng, lat]; Google Maps API wants {lat,lng}.
    // ------------------------------------------------------------------
    // EVELINA-FARM-TOUR-V1: per-product GeoJSON boundary override.
    // Reads the raw JSON from data-cs-geojson on the location card.
    // Accepts either a FeatureCollection (geojson.io export) or a bare
    // Polygon Feature / Geometry. Returns array of [lng, lat] pairs or
    // null. Always defensive — bad JSON falls through to the hard-coded
    // FINCA_BOUNDARY below.
    function parseCardGeojson(card) {
      try {
        if (!card) return null;
        var raw = card.getAttribute('data-cs-geojson');
        if (!raw) return null;
        var data = JSON.parse(raw);
        var geom = null;
        if (data && data.type === 'FeatureCollection' && data.features && data.features.length) {
          geom = data.features[0].geometry;
        } else if (data && data.type === 'Feature' && data.geometry) {
          geom = data.geometry;
        } else if (data && (data.type === 'Polygon' || data.type === 'MultiPolygon')) {
          geom = data;
        }
        if (!geom) return null;
        var coords = null;
        if (geom.type === 'Polygon') coords = geom.coordinates && geom.coordinates[0];
        else if (geom.type === 'MultiPolygon') coords = geom.coordinates && geom.coordinates[0] && geom.coordinates[0][0];
        if (!Array.isArray(coords) || coords.length < 3) return null;
        return coords.filter(function (p) { return Array.isArray(p) && p.length >= 2; });
      } catch (e) {
        if (typeof console !== 'undefined') console.warn('[farm-tour] bad GeoJSON:', e);
        return null;
      }
    }
    function boundaryCentroid(arr) {
      var sumLng = 0, sumLat = 0;
      for (var i = 0; i < arr.length; i++) { sumLng += arr[i][0]; sumLat += arr[i][1]; }
      return { lat: sumLat / arr.length, lng: sumLng / arr.length };
    }
    function boundaryPath(arr) {
      return arr.map(function (p) { return { lat: p[1], lng: p[0] }; });
    }

    const FINCA_BOUNDARY = [
      [-74.00970549740914, 10.798096418447258],
      [-74.00809819818018, 10.79883940084369 ],
      [-74.00662746685931, 10.799221210527449],
      [-74.00512521986732, 10.79984036033386 ],
      [-74.00481006315596, 10.801615249373583],
      [-74.00463147435264, 10.801945460129858],
      [-74.00571351239577, 10.803080556837685],
      [-74.0085919436949,  10.803482999185576],
      [-74.0117225003632,  10.802657475839226],
      [-74.01462194210973, 10.802543966201839],
      [-74.01597711596948, 10.7998713177915  ],
      [-74.01598762119316, 10.79882908165635 ],
      [-74.01466396300452, 10.797559818905924],
      [-74.00971600263281, 10.798065460806697]
    ];

    function fincaPath() {
      return FINCA_BOUNDARY.map(function (p) { return { lat: p[1], lng: p[0] }; });
    }

    function fincaCentroid() {
      let sumLat = 0, sumLng = 0;
      FINCA_BOUNDARY.forEach(function (p) { sumLng += p[0]; sumLat += p[1]; });
      return {
        lat: sumLat / FINCA_BOUNDARY.length,
        lng: sumLng / FINCA_BOUNDARY.length
      };
    }


    async function build3DMap(card, mapEl, lat, lng, apiKey) {
      await loadGoogleMapsScript(apiKey);

      // Import the 3D libraries we need. Older deploys split Map3DElement
      // and the marker into separate libraries; newer API exposes both
      // from `maps3d`. We destructure from one call and guard missing
      // exports.
      const maps3d = await google.maps.importLibrary('maps3d');
      const Map3DElement = maps3d.Map3DElement;
      const MapMode = maps3d.MapMode;
      const Marker3DInteractiveElement = maps3d.Marker3DInteractiveElement || maps3d.Marker3DElement;
      const Polygon3DElement = maps3d.Polygon3DInteractiveElement || maps3d.Polygon3DElement;
      const AltitudeMode = maps3d.AltitudeMode;

      if (!Map3DElement) {
        throw new Error('Map3DElement not available — API may not include maps3d library');
      }

      // Override lat/lng with the actual farm centroid. The values
      // passed in (from the location card's data-attrs) are the
      // broader Palmor village — the real Finca La Eneida sits ~30 km
      // northeast of Palmor in the Sierra Nevada, per the GeoJSON
      // polygon the merchant drew.
      // EVELINA-FARM-TOUR-V1: prefer per-card GeoJSON if present, fall back to FINCA_BOUNDARY.
      const _boundary = parseCardGeojson(card) || FINCA_BOUNDARY;
      const centroid = boundaryCentroid(_boundary);
      lat = centroid.lat;
      lng = centroid.lng;

      // ============================================================
      // CAMERA CHOREOGRAPHY — rAF-driven continuous path
      //
      // Seven keyframes. Range uses exponential interpolation (huge
      // dynamic range 2 500 km ↔ 420 m); others linear.
      //
      // The close phase now traverses the FULL LENGTH of the farm
      // polygon — BOTTOM focuses on the polygon's SOUTH edge (the
      // "beginning"), UPHILL focuses on the NORTH edge (the "end").
      // Centre interpolates smoothly across the 600 m N-S extent, so
      // the camera physically flies over the farm while the focal
      // point traverses it from south to north.
      //
      // Slower close phase (4.4 s BOTTOM→UPHILL vs previous 2 s) so
      // there's time to really look at the farm.
      //
      //   t=0.00  SPACE      range 2 500 km
      //   t=0.25  BOTTOM     near polygon SOUTH edge, tilt 82°
      //   t=0.45  UPHILL     near polygon NORTH edge, tilt 78° ← 4.4 s
      //   t=0.55  ORBIT_1   +120°   range 1 300 m    tilt 76°
      //   t=0.66  ORBIT_2   +240°   range 3 500 m    tilt 80°
      //   t=0.77  ORBIT_3   +360°   range 8 500 m    tilt 84°
      //   t=1.00  SPACE      pulled back to 2 500 km, seamless loop
      //
      // Total duration: 22 s.
      // ============================================================

      const TOTAL_MS = 22000;

      // BOTTOM centre shifted 220 m SOUTH of the farm centroid so the
      // camera aims at the LOWER/south portion of the polygon (what's
      // in the foreground of the screen) rather than the middle.
      // UPHILL drifts back toward centroid; ORBIT_* pivots on the
      // polygon centroid for a clean rotation around the farm.
      const farmCtr        = { lat: lat, lng: lng, altitude: 0 };
      const farmCloseCtr1  = { lat: lat - 0.0020, lng: lng, altitude: 1500 };  // BOTTOM — focus SOUTH part
      const farmCloseCtr2  = { lat: lat - 0.0005, lng: lng, altitude: 1700 };  // UPHILL — easing back
      // ORBIT centres dropped ~200 m each so the camera sits lower
      // during the rotation — the polygon was clipping at the bottom
      // edge of the viewport (most noticeable around heading 160°).
      // Lowering the look-at altitude tips the camera down and lifts
      // the farm up into the frame.
      const farmOrbitCtr2  = { lat: lat, lng: lng, altitude: 1700 };
      const farmOrbitCtr3  = { lat: lat, lng: lng, altitude: 1850 };
      const farmOrbitCtr4  = { lat: lat, lng: lng, altitude: 2000 };

      // Keyframes.
      //   SPACE range expanded to 10 000 km so we see ~half of Earth
      //   at the opening (camera ~8 200 km altitude at tilt 32°).
      //   All headings rotated +30° eastward from the previous pass,
      //   so the camera is permanently offset 30° east of north.
      // Ranges pulled back significantly (was 1200/1400): the farm
      // polygon is ~1240 m E–W × 660 m N–S and sits on a south-facing
      // slope that drops ~600 m from north to south end. At range 1200
      // with tilt 70° the down-angle to the polygon's south tip (which
      // is BOTH closer to camera AND ~600 m lower than camera altitude)
      // fell past the bottom FOV edge, so the south edge was clipped.
      //
      // Range 2000 puts the camera ~1.88 km SSW of target at altitude
      // ~2180 m MSL. Down-angle to the low south tip (ground ~1300 m)
      // is ~30° — within the 20° look-axis + 22.5° FOV half-angle, so
      // the south tip lands around 14 % above the bottom edge with
      // comfortable margin.
      const KF = [
        { t: 0.00, center: farmCtr,       tilt: 32, heading:  30, range: 10000000 },
        { t: 0.25, center: farmCloseCtr1, tilt: 70, heading:  30, range:     2000 },
        { t: 0.45, center: farmCloseCtr2, tilt: 68, heading:  65, range:     1800 },
        { t: 0.55, center: farmOrbitCtr2, tilt: 70, heading: 160, range:     2400 },
        { t: 0.66, center: farmOrbitCtr3, tilt: 74, heading: 255, range:     4000 },
        { t: 0.77, center: farmOrbitCtr4, tilt: 80, heading: 350, range:     7000 },
        { t: 1.00, center: farmCtr,       tilt: 32, heading: 390, range: 10000000 }
      ];

      function kfToCam(kf) {
        return {
          center:  kf.center,
          tilt:    kf.tilt,
          heading: kf.heading,
          range:   kf.range
        };
      }

      // Legacy aliases used by the controls + initial pose
      const farCamera   = kfToCam(KF[0]);   // SPACE
      const closeCamera = kfToCam(KF[1]);   // BOTTOM

      // Use SATELLITE mode for pure photorealism (no label overlay).
      // The bento already carries the place name, coords, etc. — we
      // don't need map labels fighting with our typography.
      const modeValue = (MapMode && MapMode.SATELLITE) || 'SATELLITE';

      const map = new Map3DElement({
        center:  farCamera.center,
        tilt:    farCamera.tilt,
        heading: farCamera.heading,
        range:   farCamera.range,
        mode:    modeValue,
        // `defaultUIDisabled` was renamed to `defaultUIHidden`.
        // Pass both for forward/back compatibility with the API.
        defaultUIHidden:   true,
        defaultUIDisabled: true
      });
      // `pointer-events: none` — the map is purely decorative in this
      // layout; any wheel/touch gesture over it should scroll the page
      // normally instead of being swallowed by Google Maps' internal
      // gesture handler (which was what interrupted the camera loop
      // every time a visitor scrolled with their cursor over the map).
      // The replay overlay and the Pauza button are separate DOM nodes
      // above this one with their own `pointer-events: auto` — those
      // remain fully interactive.
      map.style.cssText = 'width:100%;height:100%;display:block;border-radius:inherit;pointer-events:none';
      mapEl.appendChild(map);

      // EVELINA-FARM-TOUR-V4-COUNTRY: country-border highlight registry.
      // Each entry is the simplified [lng, lat] outline of a country we
      // grow coffee in. Renders BEFORE the farm polygon so the farm
      // stays the visual hero at close range.
      const COUNTRY_BORDERS = {
        // Colombia — Natural Earth 1:50m, RDP-simplified to 157 pts.
        // Source: https://github.com/datasets/geo-countries (CC BY 4.0).
        // Bbox approx: W -79.0  E -66.9  S -4.3  N 12.5
        CO: {
          bbox: { minLng: -79.5, maxLng: -66.5, minLat: -5, maxLat: 13.5 },
          coords: [
        [-70.091, 6.975], [-70.3, 6.968], [-70.412, 7.007], [-70.519, 7.073], [-70.705, 7.085], [-70.865, 7.075],
        [-71.072, 7.015], [-71.232, 7.035], [-71.358, 7.04], [-71.494, 7.05], [-71.695, 7.04], [-71.921, 7.04],
        [-72.027, 7.105], [-72.117, 7.254], [-72.122, 7.374], [-72.193, 7.429], [-72.273, 7.424], [-72.343, 7.424],
        [-72.419, 7.529], [-72.434, 7.673], [-72.434, 7.743], [-72.404, 7.887], [-72.439, 7.967], [-72.359, 8.062],
        [-72.313, 8.126], [-72.338, 8.211], [-72.338, 8.36], [-72.419, 8.477], [-72.545, 8.576], [-72.625, 8.711],
        [-72.721, 8.895], [-72.791, 9.113], [-72.831, 9.158], [-72.932, 9.098], [-72.932, 9.222], [-72.987, 9.282],
        [-73.067, 9.277], [-73.198, 9.192], [-73.364, 9.182], [-73.269, 9.316], [-73.258, 9.396], [-73.138, 9.51],
        [-73.062, 9.634], [-72.957, 9.864], [-72.992, 9.919], [-72.932, 10.117], [-72.886, 10.369], [-72.886, 10.458],
        [-72.796, 10.587], [-72.675, 10.903], [-72.615, 10.942], [-72.55, 10.982], [-72.444, 11.135], [-72.323, 11.159],
        [-72.238, 11.164], [-71.987, 11.65], [-71.951, 11.659], [-71.66, 11.758], [-71.348, 11.847], [-71.333, 11.965],
        [-71.142, 11.989], [-71.082, 12.092], [-71.162, 12.196], [-71.232, 12.314], [-71.413, 12.407], [-71.589, 12.441],
        [-71.685, 12.417], [-71.775, 12.368], [-71.831, 12.358], [-71.901, 12.358], [-71.926, 12.26], [-71.901, 12.205],
        [-71.921, 12.181], [-72.012, 12.156], [-72.022, 12.255], [-72.107, 12.255], [-72.152, 12.205], [-72.152, 12.092],
        [-72.178, 12.048], [-72.218, 11.9], [-72.461, 11.798], [-72.766, 11.709], [-73.111, 11.423], [-73.321, 11.289],
        [-73.776, 11.268], [-73.992, 11.342], [-74.084, 11.347], [-74.192, 11.286], [-74.192, 11.101], [-74.18, 11.057],
        [-74.246, 10.974], [-74.3, 10.898], [-74.321, 10.789], [-74.381, 10.763], [-74.453, 10.786], [-74.459, 10.874],
        [-74.459, 10.936], [-74.462, 10.989], [-74.572, 11.036], [-74.77, 11.074], [-74.875, 10.998], [-74.974, 10.951],
        [-75.034, 10.88], [-75.156, 10.819], [-75.255, 10.714], [-75.258, 10.699], [-75.441, 10.605], [-75.489, 10.543],
        [-75.48, 10.47], [-75.51, 10.414], [-75.489, 10.364], [-75.504, 10.291], [-75.587, 10.256], [-75.668, 10.15],
        [-75.614, 10.164], [-75.56, 10.206], [-75.516, 10.208], [-75.516, 10.141], [-75.557, 10.079], [-75.569, 10.023],
        [-75.572, 9.923], [-75.644, 9.749], [-75.689, 9.708], [-75.587, 9.655], [-75.575, 9.584], [-75.584, 9.463],
        [-75.638, 9.422], [-75.725, 9.419], [-75.785, 9.43], [-75.944, 9.43], [-75.965, 9.389], [-76.063, 9.362],
        [-76.111, 9.271], [-76.171, 9.235], [-76.186, 9.159], [-76.219, 9.114], [-76.258, 9.029], [-76.285, 8.94],
        [-76.462, 8.82], [-76.569, 8.711], [-76.719, 8.634], [-76.887, 8.616], [-76.938, 8.56], [-76.878, 8.512],
        [-76.797, 8.456], [-76.74, 8.397], [-76.743, 8.243], [-76.728, 8.056], [-76.731, 7.934], [-76.785, 7.914],
        [-76.836, 7.914], [-76.893, 7.949], [-76.884, 8.035], [-76.815, 8.047], [-76.815, 8.097], [-76.854, 8.118],
        [-76.881, 8.118], [-76.926, 8.115], [-76.926, 8.154], [-76.914, 8.198], [-76.962, 8.204], [-76.965, 8.26],
        [-76.998, 8.263], [-77.025, 8.281], [-77.093, 8.388], [-77.141, 8.45], [-77.252, 8.497], [-77.255, 8.542],
        [-77.297, 8.592], [-77.333, 8.66], [-77.369, 8.66], [-77.414, 8.631], [-77.417, 8.58], [-77.459, 8.51],
        [-77.432, 8.48], [-77.369, 8.386], [-77.336, 8.294], [-77.273, 8.211], [-77.213, 8.125], [-77.213, 8.054],
        [-77.207, 7.992], [-77.168, 7.948], [-77.159, 7.933], [-77.183, 7.912], [-77.252, 7.912], [-77.285, 7.897],
        [-77.309, 7.862], [-77.339, 7.808], [-77.366, 7.749], [-77.42, 7.672], [-77.474, 7.604], [-77.519, 7.557],
        [-77.56, 7.521], [-77.617, 7.566], [-77.62, 7.604], [-77.665, 7.655], [-77.707, 7.681], [-77.74, 7.681],
        [-77.746, 7.628], [-77.731, 7.578], [-77.737, 7.503], [-77.743, 7.489], [-77.785, 7.474], [-77.824, 7.474],
        [-77.845, 7.355], [-77.881, 7.221], [-77.878, 7.198], [-77.803, 7.114], [-77.719, 7.043], [-77.689, 6.981],
        [-77.704, 6.93], [-77.719, 6.903], [-77.698, 6.841], [-77.686, 6.823], [-77.671, 6.856], [-77.644, 6.856],
        [-77.593, 6.832], [-77.566, 6.808], [-77.545, 6.741], [-77.525, 6.661], [-77.507, 6.661], [-77.507, 6.688],
        [-77.456, 6.709], [-77.405, 6.67], [-77.384, 6.628], [-77.354, 6.557], [-77.348, 6.518], [-77.348, 6.483],
        [-77.378, 6.39], [-77.387, 6.358], [-77.39, 6.289], [-77.396, 6.257], [-77.465, 6.277], [-77.474, 6.254],
        [-77.474, 6.155], [-77.414, 6.078], [-77.348, 6.006], [-77.312, 5.944], [-77.276, 5.896], [-77.273, 5.837],
        [-77.246, 5.773], [-77.249, 5.719], [-77.312, 5.642], [-77.363, 5.618], [-77.441, 5.618], [-77.489, 5.591],
        [-77.557, 5.505], [-77.557, 5.466], [-77.495, 5.457], [-77.417, 5.442], [-77.396, 5.385], [-77.366, 5.275],
        [-77.366, 5.162], [-77.336, 4.928], [-77.33, 4.853], [-77.318, 4.772], [-77.288, 4.704], [-77.324, 4.638],
        [-77.33, 4.531], [-77.378, 4.406], [-77.39, 4.361], [-77.444, 4.308], [-77.504, 4.269], [-77.504, 4.23],
        [-77.435, 4.239], [-77.36, 4.254], [-77.333, 4.23], [-77.285, 4.218], [-77.297, 4.179], [-77.42, 4.149],
        [-77.438, 4.09], [-77.411, 4.031], [-77.399, 3.968], [-77.336, 3.965], [-77.3, 4.058], [-77.237, 4.072],
        [-77.195, 4.028], [-77.225, 3.977], [-77.288, 3.965], [-77.297, 3.89], [-77.267, 3.837], [-77.192, 3.837],
        [-77.102, 3.887], [-77.045, 3.899], [-77.063, 3.857], [-77.12, 3.816], [-77.126, 3.771], [-77.174, 3.72],
        [-77.174, 3.69], [-77.126, 3.666], [-77.162, 3.645], [-77.201, 3.627], [-77.222, 3.571], [-77.234, 3.562],
        [-77.267, 3.506], [-77.258, 3.473], [-77.288, 3.473], [-77.315, 3.434], [-77.339, 3.395], [-77.339, 3.335],
        [-77.402, 3.362], [-77.438, 3.317], [-77.459, 3.287], [-77.516, 3.264], [-77.528, 3.183], [-77.578, 3.144],
        [-77.652, 3.026], [-77.677, 2.993], [-77.673, 2.964], [-77.659, 2.95], [-77.659, 2.919], [-77.686, 2.895],
        [-77.686, 2.854], [-77.728, 2.826], [-77.748, 2.746], [-77.766, 2.715], [-77.766, 2.664], [-77.773, 2.626],
        [-77.826, 2.597], [-77.889, 2.626], [-77.949, 2.643], [-77.978, 2.634], [-78.042, 2.6], [-78.042, 2.511],
        [-78.109, 2.48], [-78.213, 2.578], [-78.249, 2.561], [-78.277, 2.617], [-78.325, 2.636], [-78.4, 2.581],
        [-78.428, 2.53], [-78.481, 2.486], [-78.517, 2.408], [-78.554, 2.368], [-78.576, 2.321], [-78.604, 2.252],
        [-78.635, 2.252], [-78.663, 2.204], [-78.665, 2.14], [-78.654, 2.037], [-78.626, 2.003], [-78.57, 2.003],
        [-78.526, 1.992], [-78.548, 1.956], [-78.556, 1.892], [-78.501, 1.864], [-78.501, 1.806], [-78.556, 1.786],
        [-78.615, 1.794], [-78.716, 1.806], [-78.825, 1.797], [-78.853, 1.736], [-78.956, 1.666], [-78.995, 1.646],
        [-78.948, 1.582], [-78.914, 1.56], [-78.85, 1.537], [-78.811, 1.501], [-78.808, 1.434], [-78.763, 1.387],
        [-78.727, 1.356], [-78.674, 1.317], [-78.635, 1.266], [-78.601, 1.239], [-78.57, 1.222], [-78.54, 1.213],
        [-78.526, 1.188], [-78.492, 1.185], [-78.431, 1.158], [-78.38, 1.119], [-78.311, 1.041], [-78.271, 1.005],
        [-78.21, 0.985], [-78.185, 0.957], [-78.14, 0.949], [-78.079, 0.918], [-78.023, 0.882], [-77.928, 0.87],
        [-77.88, 0.84], [-77.847, 0.809], [-77.791, 0.801], [-77.687, 0.824], [-77.671, 0.815], [-77.634, 0.787],
        [-77.606, 0.723], [-77.595, 0.676], [-77.5, 0.659], [-77.422, 0.6], [-77.405, 0.539], [-77.405, 0.463],
        [-77.388, 0.407], [-77.355, 0.368], [-77.299, 0.349], [-77.226, 0.321], [-77.198, 0.312], [-77.159, 0.318],
        [-77.114, 0.326], [-77.078, 0.329], [-77.042, 0.312], [-76.991, 0.281], [-76.922, 0.265], [-76.871, 0.223],
        [-76.852, 0.223], [-76.779, 0.218], [-76.737, 0.193], [-76.701, 0.193], [-76.67, 0.249], [-76.645, 0.249],
        [-76.606, 0.24], [-76.502, 0.226], [-76.433, 0.229], [-76.393, 0.235], [-76.393, 0.279], [-76.385, 0.31],
        [-76.363, 0.369], [-76.307, 0.372], [-76.29, 0.408], [-76.265, 0.408], [-76.175, 0.374], [-76.111, 0.372],
        [-76.097, 0.313], [-76.05, 0.313], [-76.002, 0.307], [-75.971, 0.268], [-75.938, 0.187], [-75.862, 0.126],
        [-75.821, 0.106], [-75.765, 0.064], [-75.703, 0.042], [-75.558, 0.028], [-75.494, -0.017], [-75.407, -0.092],
        [-75.342, -0.096], [-75.245, -0.119], [-75.169, -0.089], [-75.109, -0.102], [-75.059, -0.132], [-74.962, -0.206],
        [-74.882, -0.286], [-74.805, -0.219], [-74.742, -0.266], [-74.752, -0.366], [-74.649, -0.396], [-74.562, -0.452],
        [-74.445, -0.526], [-74.338, -0.673], [-74.315, -0.753], [-74.318, -0.809], [-74.285, -0.836], [-74.262, -0.866],
        [-74.285, -0.929], [-74.245, -0.969], [-74.222, -1.026], [-74.198, -1.056], [-74.015, -1.061], [-73.952, -1.138],
        [-73.878, -1.168], [-73.821, -1.231], [-73.735, -1.271], [-73.635, -1.294], [-73.531, -1.384], [-73.531, -1.444],
        [-73.465, -1.494], [-73.445, -1.644], [-73.448, -1.694], [-73.428, -1.767], [-73.368, -1.814], [-73.154, -1.82],
        [-73.078, -1.89], [-73.124, -1.974], [-73.051, -2.09], [-73.081, -2.157], [-73.141, -2.234], [-73.141, -2.297],
        [-73.044, -2.364], [-72.934, -2.384], [-72.904, -2.433], [-72.848, -2.46], [-72.794, -2.433], [-72.771, -2.414],
        [-72.728, -2.414], [-72.641, -2.437], [-72.591, -2.387], [-72.514, -2.4], [-72.471, -2.447], [-72.371, -2.447],
        [-72.331, -2.487], [-72.277, -2.483], [-72.227, -2.44], [-72.071, -2.437], [-72.031, -2.394], [-71.971, -2.384],
        [-71.907, -2.41], [-71.797, -2.284], [-71.734, -2.22], [-71.69, -2.237], [-71.66, -2.264], [-71.64, -2.24],
        [-71.597, -2.24], [-71.497, -2.287], [-71.407, -2.327], [-71.38, -2.43], [-71.357, -2.43], [-71.304, -2.42],
        [-71.23, -2.41], [-71.177, -2.42], [-71.147, -2.35], [-71.01, -2.31], [-70.99, -2.297], [-70.953, -2.297],
        [-70.9, -2.31], [-70.84, -2.334], [-70.773, -2.37], [-70.693, -2.374], [-70.667, -2.433], [-70.63, -2.467],
        [-70.63, -2.517], [-70.587, -2.52], [-70.554, -2.498], [-70.517, -2.498], [-70.473, -2.51], [-70.41, -2.554],
        [-70.329, -2.585], [-70.304, -2.629], [-70.247, -2.642], [-70.216, -2.685], [-70.178, -2.723], [-70.122, -2.723],
        [-70.078, -2.748], [-70.091, -2.836], [-70.141, -2.917], [-70.203, -2.986], [-70.222, -3.073], [-70.279, -3.161],
        [-70.448, -3.405], [-70.523, -3.524], [-70.755, -3.805], [-70.586, -3.805], [-70.536, -3.837], [-70.492, -3.88],
        [-70.417, -3.855], [-70.354, -3.799], [-70.229, -3.862], [-70.166, -3.955], [-70.153, -4.043], [-70.078, -4.099],
        [-69.997, -4.155], [-69.94, -4.224], [-69.921, -4.118], [-69.903, -3.899], [-69.865, -3.743], [-69.815, -3.461],
        [-69.74, -3.149], [-69.502, -1.868], [-69.439, -1.574], [-69.389, -1.449], [-69.389, -1.361], [-69.376, -1.273],
        [-69.395, -1.236], [-69.408, -1.173], [-69.426, -1.117], [-69.495, -1.01], [-69.533, -0.947], [-69.564, -0.891],
        [-69.596, -0.847], [-69.564, -0.772], [-69.564, -0.64], [-69.633, -0.571], [-69.74, -0.502], [-69.809, -0.471],
        [-69.909, -0.402], [-70.022, -0.321], [-70.034, -0.161], [-69.997, -0.061], [-70.003, 0.033], [-70.041, 0.109],
        [-70.041, 0.159], [-70.022, 0.547], [-69.846, 0.56], [-69.765, 0.585], [-69.733, 0.604], [-69.696, 0.641],
        [-69.627, 0.654], [-69.577, 0.647], [-69.527, 0.71], [-69.439, 0.704], [-69.401, 0.673], [-69.289, 0.673],
        [-69.27, 0.597], [-69.182, 0.604], [-69.101, 0.647], [-69.138, 0.698], [-69.182, 0.71], [-69.169, 0.779],
        [-69.169, 0.911], [-69.226, 1.011], [-69.282, 1.092], [-69.389, 1.08], [-69.545, 1.08], [-69.608, 1.092],
        [-69.683, 1.099], [-69.752, 1.124], [-69.834, 1.061], [-69.796, 1.731], [-69.759, 1.738], [-69.627, 1.75],
        [-69.508, 1.769], [-69.389, 1.763], [-68.154, 1.763], [-68.192, 1.869], [-68.217, 1.932], [-68.217, 2.001],
        [-68.179, 2.038], [-68.092, 2.019], [-68.01, 1.913], [-68.01, 1.819], [-67.954, 1.8], [-67.785, 1.8],
        [-67.653, 1.938], [-67.546, 2.113], [-67.534, 2.126], [-67.434, 2.157], [-67.352, 2.138], [-67.264, 2.107],
        [-67.233, 1.944], [-67.139, 1.863], [-67.083, 1.675], [-67.083, 1.418], [-67.095, 1.211], [-66.87, 1.243],
        [-66.87, 1.387], [-66.907, 1.424], [-66.92, 1.578], [-66.951, 1.741], [-67.058, 1.954], [-67.127, 2.023],
        [-67.076, 2.091], [-67.108, 2.179], [-67.158, 2.223], [-67.202, 2.342], [-67.183, 2.411], [-67.202, 2.455],
        [-67.289, 2.498], [-67.44, 2.674], [-67.559, 2.774], [-67.628, 2.887], [-67.853, 2.887], [-67.797, 2.955],
        [-67.615, 3.087], [-67.515, 3.193], [-67.346, 3.365], [-67.289, 3.39], [-67.277, 3.484], [-67.358, 3.547],
        [-67.434, 3.615], [-67.521, 3.753], [-67.578, 3.778], [-67.628, 3.841], [-67.678, 3.922], [-67.678, 4.028],
        [-67.722, 4.159], [-67.772, 4.253], [-67.772, 4.384], [-67.822, 4.534], [-67.841, 4.609], [-67.803, 4.709],
        [-67.772, 4.89], [-67.759, 5.053], [-67.791, 5.234], [-67.835, 5.349], [-67.734, 5.405], [-67.622, 5.542],
        [-67.622, 5.605], [-67.672, 5.673], [-67.578, 5.867], [-67.44, 5.985], [-67.446, 6.054], [-67.465, 6.122],
        [-67.477, 6.228], [-67.722, 6.265], [-67.822, 6.265], [-67.91, 6.247], [-67.96, 6.209], [-68.079, 6.234],
        [-68.198, 6.234], [-68.317, 6.178], [-68.399, 6.209], [-68.48, 6.178], [-68.574, 6.178], [-68.668, 6.185],
        [-68.75, 6.203], [-68.869, 6.216], [-69.013, 6.228], [-69.075, 6.228], [-69.163, 6.122], [-69.257, 6.091],
        [-69.276, 6.122], [-69.282, 6.16], [-69.339, 6.16], [-69.395, 6.135], [-69.451, 6.166], [-69.489, 6.222],
        [-69.633, 6.403], [-69.74, 6.558], [-69.884, 6.732], [-69.978, 6.863], [-70.091, 6.975]
          ]
        }
        // To add another country later:
        //   ET: { bbox: {...}, coords: [[lng, lat], ...] }
      };

      // Ray-casting point-in-polygon: coords are [lng, lat] pairs.
      function pointInRing(x, y, ring) {
        let inside = false;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
          const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
          const hit = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
          if (hit) inside = !inside;
        }
        return inside;
      }
      function pickCountryForCentroid(lat, lng) {
        const keys = Object.keys(COUNTRY_BORDERS);
        for (let i = 0; i < keys.length; i++) {
          const c = COUNTRY_BORDERS[keys[i]];
          const b = c.bbox;
          // Fast bbox reject, then precise polygon test so a farm near a
          // shared border (Ecuador, Venezuela, Panama) never wrongly
          // triggers a neighbour country's outline.
          if (lat < b.minLat || lat > b.maxLat || lng < b.minLng || lng > b.maxLng) continue;
          if (pointInRing(lng, lat, c.coords)) return c;
        }
        return null;
      }

      const _country = pickCountryForCentroid(lat, lng);
      if (Polygon3DElement && _country) {
        try {
          const countryPoly = new Polygon3DElement({
            outerCoordinates: _country.coords.map(function (p) {
              return { lat: p[1], lng: p[0] };
            }),
            // Warm gold so it harmonises with the farm-boundary's
            // #D9B88A but reads as "country" not "farm".
            strokeColor:  'rgba(255, 215, 163, 0.72)',
            strokeWidth:  2,
            fillColor:    'rgba(255, 215, 163, 0.03)',
            altitudeMode: (AltitudeMode && AltitudeMode.CLAMP_TO_GROUND) || 'CLAMP_TO_GROUND',
            extruded:     false,
            // Draws the back-side too so the Andes don't carve chunks
            // out of the country outline at orbit altitudes.
            drawsOccludedSegments: true
          });
          map.append(countryPoly);
        } catch (err) {
          if (window.console) console.warn('[coffee-story] Country border failed:', err);
        }
      }

      // Farm boundary — gold outline clamped to terrain. Subtle fill
      // (6% alpha) so trees are still legible through it at close
      // range, outline still readable at any altitude.
      if (Polygon3DElement) {
        try {
          const polygon = new Polygon3DElement({
            outerCoordinates: boundaryPath(_boundary),
            strokeColor:  '#D9B88A',
            strokeWidth:  4,
            fillColor:    'rgba(217, 184, 138, 0.06)',
            altitudeMode: (AltitudeMode && AltitudeMode.CLAMP_TO_GROUND) || 'CLAMP_TO_GROUND',
            extruded:     false,
            drawsOccludedSegments: true
          });
          map.append(polygon);
        } catch (err) {
          if (window.console) console.warn('[coffee-story] Farm polygon failed:', err);
        }
      }

      injectMapBadge(mapEl.parentElement || mapEl);
      // EVELINA-FARM-TOUR-V7-CTRYBADGE: also mount a country-name badge that fades in
      // during SPACE keyframes (camera range > 100 km).
      injectCountryBadge(mapEl.parentElement || mapEl);

      // ============================================================
      // LOOP CONTROLLER
      //   Chains: hold-wide → dive → orbit → fly-out → repeat.
      //   Stops on user interaction (pointer/wheel on map).
      //   Pauses when tab hidden or map scrolls off-screen.
      //   Restarts when the recenter button is tapped.
      // ============================================================
      let looping     = true;
      let tilesLoaded = false;
      let loopTimers  = [];

      function clearLoopTimers() {
        loopTimers.forEach(clearTimeout);
        loopTimers = [];
      }

      function setCamera(cam) {
        try {
          map.center  = cam.center;
          map.tilt    = cam.tilt;
          map.heading = cam.heading;
          map.range   = cam.range;
        } catch (_) {}
      }

      // Linear interp helper
      function lerp(a, b, t) { return a + (b - a) * t; }

      // requestAnimationFrame handle so stopLoop() can cancel cleanly
      let rafId = null;

      function runLoop() {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        clearLoopTimers();
        if (!looping || reduceMotion || document.hidden) return;

        const host  = mapEl.parentElement || mapEl;
        const finca = host.querySelector('.cs-map-badge');

        if (finca) {
          finca.classList.remove('is-visible');
          finca.classList.add('is-hidden');
        }

        const t0 = performance.now();

        // EVELINA-FARM-TOUR-V7-CTRYBADGE: country badge element + cached visibility state.
        const countryBadgeEl = (mapEl.parentElement || mapEl).querySelector('.cs-country-badge');
        let countryBadgeVisible = false;

        function frame(now) {
          if (!looping || document.hidden) {
            rafId = null;
            return;
          }

          const elapsed = now - t0;
          const t = Math.min(elapsed / TOTAL_MS, 1);

          // Locate the segment t falls into
          let i = 0;
          while (i < KF.length - 1 && KF[i + 1].t <= t) i++;
          const a = KF[i];
          const b = KF[Math.min(i + 1, KF.length - 1)];
          const local = (b.t === a.t) ? 1 : (t - a.t) / (b.t - a.t);

          // Directly drive the Map3DElement's camera each frame —
          // continuous motion, no landing feel at waypoints.
          try {
            map.center = {
              lat:      lerp(a.center.lat,      b.center.lat,      local),
              lng:      lerp(a.center.lng,      b.center.lng,      local),
              altitude: lerp(a.center.altitude, b.center.altitude, local)
            };
            map.tilt    = lerp(a.tilt,    b.tilt,    local);
            // Headings in KF can be negative / > 360 to force short-
            // arc interpolation; normalise to 0-360 before writing.
            const rawH = lerp(a.heading, b.heading, local);
            map.heading = ((rawH % 360) + 360) % 360;
            // Range spans 6 250× (2 500 000 → 400). Exponential
            // interpolation makes the zoom feel linear to the eye.
            map.range = a.range * Math.pow(b.range / a.range, local);

          // EVELINA-FARM-TOUR-V7-CTRYBADGE: show country badge when camera is in SPACE
          // (range > 100 km — when the country outline is most visible).
          if (countryBadgeEl) {
            var inSpace = map.range > 100000;
            if (inSpace !== countryBadgeVisible) {
              countryBadgeEl.classList.toggle('is-visible', inSpace);
              countryBadgeVisible = inSpace;
            }
          }
          } catch (_) {}

          if (t < 1) {
            rafId = requestAnimationFrame(frame);
          } else if (looping) {
            runLoop();
          }
        }

        // ---- Finca La Eneida badge choreography ----
        //   Show: just before BOTTOM lands (t=0.25 × 22 s = 5.5 s)
        //   Hide: during ORBIT_3 → SPACE sweep (t=0.80 × 22 s ≈ 17.6 s)
        loopTimers.push(setTimeout(function () {
          if (!looping || !finca) return;
          finca.classList.remove('is-hidden');
          finca.classList.add('is-visible');
        }, 4400));
        loopTimers.push(setTimeout(function () {
          if (!looping || !finca) return;
          finca.classList.remove('is-visible');
          finca.classList.add('is-leaving');
        }, 17000));
        loopTimers.push(setTimeout(function () {
          if (!finca) return;
          finca.classList.remove('is-leaving');
          finca.classList.add('is-hidden');
        }, 17800));

        rafId = requestAnimationFrame(frame);
      }

      function emitState() {
        document.dispatchEvent(new CustomEvent('cs:map-loop-state', {
          detail: { looping: looping }
        }));
      }

      function stopLoop() {
        const wasLooping = looping;
        looping = false;
        clearLoopTimers();
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        try { map.stopCameraAnimation(); } catch (_) {}
        if (wasLooping) emitState();
      }

      function resumeLoop() {
        const wasLooping = looping;
        looping = true;
        runLoop();
        if (!wasLooping) emitState();
      }

      // External resume — fires when user clicks the central play
      // overlay on the map. Only resumes (never stops), so a click is
      // always safe.
      document.addEventListener('cs:map-resume', function () {
        if (!looping) resumeLoop();
      });
      // Push the initial state so any UI that mounts later (the play
      // overlay's `.is-paused` class on the map-wrap) is in sync.
      window.setTimeout(emitState, 0);

      // Pause when the tab is backgrounded — releases CPU/GPU and
      // signals the play overlay to fade in. When the tab returns,
      // we DON'T auto-resume; the play overlay invites the user to
      // restart the animation deliberately.
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          stopLoop();
        }
      });

      // ── SHIELD: prevent Google Maps from ever seeing a gesture ──
      // A transparent overlay (`.cs-location__shield`) sits above the
      // <gmp-map-3d> element and absorbs every wheel / pointer / touch
      // event with capture-phase listeners, calling stopImmediate­-
      // Propagation so Google's shadow-DOM handlers never receive the
      // gesture. We don't preventDefault on wheel — the page still
      // scrolls normally when the cursor is over the map.
      const shield = card.querySelector('[data-cs-map-shield]');
      if (shield) {
        const SWALLOW = ['wheel', 'pointerdown', 'pointermove', 'pointerup',
                         'mousedown', 'mousemove', 'mouseup',
                         'touchstart', 'touchmove', 'touchend',
                         'click', 'dblclick', 'contextmenu',
                         'gesturestart', 'gesturechange', 'gestureend'];
        SWALLOW.forEach(function (evt) {
          // `passive: true` on wheel so we don\'t block page scroll;
          // capture phase + stopImmediatePropagation is enough to
          // kill Google\'s internal handler (which is registered in
          // the bubble phase on its shadow-DOM root).
          const passive = (evt === 'wheel' || evt === 'touchmove' || evt === 'touchstart');
          shield.addEventListener(evt, function (e) {
            e.stopImmediatePropagation();
            e.stopPropagation();
          }, { capture: true, passive: passive });
        });
      }

      // ============================================================
      // LOOP-START DETECTION
      //   Start the cinematic as soon as there's any signal the map
      //   is alive: either a canvas in the shadow DOM, or a steady
      //   camera event, or 1.5 s elapsed (failsafe). NO 2D fallback
      //   after this point — if the Maps JS API loaded at all (which
      //   is confirmed by the fact that we got this far in build3DMap),
      //   3D is working. Google will show its own overlay if tiles
      //   themselves ever fail, and our earlier fallback was firing
      //   spuriously because Google's shadow DOM is sometimes closed
      //   to querySelector, which left `tilesLoaded` permanently
      //   false and tore down a perfectly functional 3D map.
      // ============================================================
      let loopStarted = false;

      function maybeStartLoop() {
        if (loopStarted) return;
        loopStarted = true;
        tilesLoaded = true;
        clearInterval(pollInterval);
        clearTimeout(forceStartTimer);
        // Tell the loader it can finalise: the wrap class is observed
        // by the loader's MutationObserver, which jumps the bar to
        // 100 % and fades the overlay out.
        if (mapEl && mapEl.parentElement) {
          mapEl.parentElement.classList.add('is-loaded');
        }
        const wrap = card && card.querySelector('[data-cs-location-map-wrap]');
        if (wrap) wrap.classList.add('is-loaded');
        if (reduceMotion) {
          setCamera(closeCamera);
        } else if (looping) {
          runLoop();
        }
      }

      map.addEventListener('gmp-steadychange', function (e) {
        if (e.isSteady) maybeStartLoop();
      });

      const pollInterval = setInterval(function () {
        const canvas = map.shadowRoot && map.shadowRoot.querySelector('canvas');
        if (canvas) maybeStartLoop();
      }, 250);

      // Failsafe: after 1.5 s we force-start the loop regardless.
      // If the Maps API is still initialising, the first leg will
      // simply blend with whatever the API then settles on.
      const forceStartTimer = setTimeout(maybeStartLoop, 1500);

      card.classList.add('is-map-ready');

      // Expose the loop controls on the map element so our custom
      // control bar can restart/stop the loop when the user taps
      // recenter or zoom.
      map.__csLoop = {
        start: resumeLoop,
        stop:  stopLoop
      };

      injectCustomControls3D(map, mapEl.parentElement || mapEl, closeCamera);
    }

    // Custom branded badge overlaying the map in the top-left corner.
    // Carries the farm name + region + altitude in our own typography
    // (Fraunces italic for the name, JetBrains Mono for labels). Uses
    // the same creamy surface as the hero product card so the whole
    // page shares one visual language.
    function injectMapBadge(host) {
      if (!host || host.querySelector('.cs-map-badge')) return;
      // EVELINA-FARM-TOUR-V6-DYNBADGE: read data attributes from the closest
      // [data-cs-location] card so the badge content is dynamic.
      // Falls back to the original hardcoded "Finca La Eneida ·
      // Palmor" when those attributes are absent (preserves the
      // behaviour on the original Palmor coffee-story page).
      var card = host.closest ? host.closest('[data-cs-location]') : null;
      if (!card && host.querySelector) card = host.querySelector('[data-cs-location]');
      var eyebrow  = (card && card.getAttribute('data-badge-eyebrow')) || 'Finca';
      var name     = (card && card.getAttribute('data-badge-name')) || 'La Eneida';
      var region   = (card && card.getAttribute('data-badge-region')) || 'Palmor · Sierra Nevada';
      var altitude = (card && card.getAttribute('data-badge-altitude')) || '1 500+ m n. m.';
      function esc(s) {
        return String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }
      const badge = document.createElement('aside');
      badge.className = 'cs-map-badge';
      badge.setAttribute('aria-label', name);
      badge.innerHTML =
        '<div class="cs-map-badge__header">' +
          '<span class="cs-map-badge__mark" aria-hidden="true">' +
            '<svg viewBox="0 0 16 16" width="11" height="11">' +
              '<circle cx="8" cy="8" r="2.3" fill="currentColor"/>' +
              '<circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" stroke-width="0.9" opacity="0.55"/>' +
              '<circle cx="8" cy="8" r="7.3" fill="none" stroke="currentColor" stroke-width="0.6" opacity="0.22"/>' +
            '</svg>' +
          '</span>' +
          '<div class="cs-map-badge__titles">' +
            '<div class="cs-map-badge__eyebrow">' + esc(eyebrow) + '</div>' +
            '<div class="cs-map-badge__name">' + esc(name) + '</div>' +
          '</div>' +
        '</div>' +
        '<div class="cs-map-badge__rule" aria-hidden="true"></div>' +
        '<dl class="cs-map-badge__stats">' +
          '<div class="cs-map-badge__stat">' +
            '<dt class="cs-map-badge__stat-label">Region</dt>' +
            '<dd class="cs-map-badge__stat-value">' + esc(region) + '</dd>' +
          '</div>' +
          '<div class="cs-map-badge__stat">' +
            '<dt class="cs-map-badge__stat-label">Nadm. výška</dt>' +
            '<dd class="cs-map-badge__stat-value">' + esc(altitude) + '</dd>' +
          '</div>' +
        '</dl>';
      host.appendChild(badge);
    }

    // EVELINA-FARM-TOUR-V7-CTRYBADGE: floating country-name pill that lights up when
    // the camera retreats to SPACE (where the country border is visible).
    function injectCountryBadge(host) {
      if (!host || host.querySelector('.cs-country-badge')) return;
      var card = host.closest ? host.closest('[data-cs-location]') : null;
      if (!card && host.querySelector) card = host.querySelector('[data-cs-location]');
      var country = (card && card.getAttribute('data-badge-country')) || '';
      if (!country) return;
      var badge = document.createElement('div');
      badge.className = 'cs-country-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.innerHTML =
        '<span class="cs-country-badge__dot"></span>' +
        '<span class="cs-country-badge__name">' +
          String(country).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') +
        '</span>';
      host.appendChild(badge);
    }

    function injectCustomControls3D(map, host, initialCamera) {
      // If a previous build added 2D controls, remove them
      const prev = host.querySelector('.cs-location__controls');
      if (prev) prev.remove();

      const c = document.createElement('div');
      c.className = 'cs-location__controls cs-location__controls--3d';
      c.innerHTML =
        '<a href="#" class="cs-location__ctrl" data-cs-ctrl="in" title="Přiblížit" aria-label="Přiblížit" role="button">' +
          '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M8 3v10 M3 8h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
        '</a>' +
        '<a href="#" class="cs-location__ctrl" data-cs-ctrl="out" title="Oddálit" aria-label="Oddálit" role="button">' +
          '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 8h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
        '</a>' +
        '<a href="#" class="cs-location__ctrl cs-location__ctrl--reset" data-cs-ctrl="reset" title="Zpět na původní pohled" aria-label="Zpět na původní pohled" role="button">' +
          '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
            '<circle cx="8" cy="8" r="1.5" fill="currentColor"/>' +
            '<circle cx="8" cy="8" r="4.5" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
            '<path d="M8 1.5v1.8 M8 12.7v1.8 M1.5 8h1.8 M12.7 8h1.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>' +
          '</svg>' +
        '</a>';

      host.appendChild(c);

      function currentCam() {
        return {
          center:  map.center,
          tilt:    map.tilt,
          heading: map.heading,
          range:   map.range
        };
      }

      c.querySelectorAll('[data-cs-ctrl]').forEach(function (btn) {
        btn.addEventListener('click', function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          try { map.stopCameraAnimation(); } catch (_) {}

          const action = btn.getAttribute('data-cs-ctrl');
          // Zoom buttons stop the loop (user is exploring).
          // Recenter re-enables the loop from the wide view.
          if (action === 'in' || action === 'out') {
            if (map.__csLoop && map.__csLoop.stop) map.__csLoop.stop();
          }
          if (action === 'in') {
            const cam = currentCam();
            map.flyCameraTo({
              endCamera: {
                center: cam.center, tilt: cam.tilt, heading: cam.heading,
                range: Math.max(300, cam.range * 0.6)
              },
              durationMillis: 600
            });
          } else if (action === 'out') {
            const cam = currentCam();
            map.flyCameraTo({
              endCamera: {
                center: cam.center, tilt: cam.tilt, heading: cam.heading,
                range: Math.min(120000, cam.range * 1.6)
              },
              durationMillis: 600
            });
          } else if (action === 'reset') {
            if (map.__csLoop && map.__csLoop.start) {
              map.__csLoop.start();
            } else {
              map.flyCameraTo({
                endCamera: initialCamera,
                durationMillis: 900
              });
            }
          }
        });
      });
    }

    // Google's official inline loader for the Maps JS API. We only
    // load it once per page, with version "alpha" to get the latest
    // 3D-maps features (`maps3d` library, flyCameraAround, etc.).
    let _gmapsPromise = null;
    function loadGoogleMapsScript(apiKey) {
      if (window.google && window.google.maps && window.google.maps.importLibrary) {
        return Promise.resolve();
      }
      if (_gmapsPromise) return _gmapsPromise;

      _gmapsPromise = new Promise(function (resolve, reject) {
        (function (g) {
          let h, a, k, p = 'The Google Maps JavaScript API',
              c = 'google', l = 'importLibrary', q = '__ib__',
              m = document, b = window;
          b = b[c] || (b[c] = {});
          const d = b.maps || (b.maps = {});
          const r = new Set();
          const e = new URLSearchParams();
          const u = function () {
            return h || (h = new Promise(async function (f, n) {
              a = m.createElement('script');
              e.set('libraries', [...r] + '');
              for (k in g) e.set(k.replace(/[A-Z]/g, function (t) { return '_' + t[0].toLowerCase(); }), g[k]);
              e.set('callback', c + '.maps.' + q);
              a.src = 'https://maps.' + c + 'apis.com/maps/api/js?' + e;
              d[q] = f;
              a.onerror = function () { h = n(Error(p + ' could not load.')); };
              a.nonce = (m.querySelector('script[nonce]') || {}).nonce || '';
              m.head.append(a);
            }));
          };
          if (d[l]) {
            // Already configured; just resolve.
          } else {
            d[l] = function (f) {
              const args = Array.prototype.slice.call(arguments, 1);
              r.add(f);
              return u().then(function () { return d[l].apply(d, [f].concat(args)); });
            };
          }
        // `beta` channel: Map3DElement + Polygon3DElement are stable
        // enough here and it silences the "alpha for dev only" console
        // warning that shows on the `alpha` channel. Interactive-variant
        // classes fall back to non-interactive versions if missing.
        })({ key: apiKey, v: 'beta' });

        // Wait until importLibrary is wired up
        const start = Date.now();
        const check = setInterval(function () {
          if (window.google && window.google.maps && window.google.maps.importLibrary) {
            clearInterval(check);
            resolve();
          } else if (Date.now() - start > 15000) {
            clearInterval(check);
            reject(new Error('Google Maps took too long to load'));
          }
        }, 60);
      });

      return _gmapsPromise;
    }

    // Optional hardcoded fallback — if you prefer to not expose the key
    // via a meta tag, paste it here and it will be used. Leave empty to
    // rely on the <meta name="cs-google-maps-key"> produced from the
    // theme section setting.
    const GMAPS_KEY_FALLBACK = 'AIzaSyBIk7z3Kx-Fbbxf8LuOO1RD2QK9TGxm2h8';

    // --- CLOCK -----------------------------------------------------
    function startClock(card, tz) {
      // Time now lives in the right-column stats grid outside the card,
      // so scope to the nearest bento (or document as fallback).
      const scope = card.closest('[data-cs-bento]') || document;
      const el = scope.querySelector('[data-cs-location-time]');
      if (!el) return;

      let fmt;
      try {
        fmt = new Intl.DateTimeFormat('cs-CZ', {
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          timeZone: tz,
          hour12: false
        });
      } catch (_) {
        // Invalid timezone — fall back to UTC
        fmt = new Intl.DateTimeFormat('cs-CZ', {
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          timeZone: 'UTC',
          hour12: false
        });
      }

      function tick() {
        try {
          el.textContent = fmt.format(new Date());
        } catch (_) {}
      }
      tick();
      // Update frequency: 1 s normally, 15 s under reduced motion so the
      // page doesn't repaint constantly when users want calm.
      setInterval(tick, reduceMotion ? 15000 : 1000);
    }

    // --- WEATHER ---------------------------------------------------
    function startWeather(card, lat, lng) {
      // Resolve the widgets. Wind now lives in a sibling stat-tile
      // (right column of the bento), so we climb to the nearest bento
      // or fall back to document scope.
      const scope = card.closest('[data-cs-bento]') || document;
      const el      = scope.querySelector('[data-cs-location-weather]');
      const windEl  = scope.querySelector('[data-cs-location-wind]');
      if (!el && !windEl) return;

      // Cardinal compass direction (8-point) for wind.
      function compass(deg) {
        if (deg == null || isNaN(deg)) return '';
        const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
        return ['S','SV','V','JV','J','JZ','Z','SZ'][i];
      }

      function render(data) {
        if (!data) {
          if (el) el.innerHTML = '<span class="cs-location__weather-desc">—</span>';
          if (windEl) windEl.textContent = '—';
          return;
        }
        const [desc, icon] = WX[data.code] || ['—', 'cloud'];
        const svg  = ICONS[icon] || ICONS['cloud'];
        const temp = Math.round(data.temp);
        if (el) {
          el.innerHTML =
            svg +
            '<span class="cs-location__weather-temp">' + temp + '°</span>' +
            '<span class="cs-location__weather-desc">' + desc + '</span>';
        }
        if (windEl && data.wind != null) {
          const kmh = Math.round(data.wind);
          const dir = compass(data.windDir);
          windEl.innerHTML =
            '<span class="cs-bento__stat-num">' + kmh + '</span>' +
            '<span class="cs-bento__stat-unit">km/h' + (dir ? ' · ' + dir : '') + '</span>';
        }
      }

      function fetchWx() {
        const url =
          'https://api.open-meteo.com/v1/forecast' +
          '?latitude='  + encodeURIComponent(lat) +
          '&longitude=' + encodeURIComponent(lng) +
          '&current=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m' +
          '&wind_speed_unit=kmh' +
          '&timezone=auto';
        fetch(url, { mode: 'cors' })
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
          .then(function (json) {
            if (!json || !json.current) throw new Error('no current');
            render({
              temp: json.current.temperature_2m,
              code: json.current.weather_code,
              wind: json.current.wind_speed_10m,
              windDir: json.current.wind_direction_10m
            });
          })
          .catch(function () {
            // Network / rate-limit. Leave the skeleton visible.
          });
      }

      fetchWx();
      // Refresh every 10 minutes while the tab is visible
      setInterval(function () {
        if (!document.hidden) fetchWx();
      }, 10 * 60 * 1000);
    }

    // --- LEAFLET LOADER --------------------------------------------
    let _leafletPromise = null;
    function ensureLeaflet(cb) {
      if (window.L) { cb(); return; }
      if (!_leafletPromise) {
        _leafletPromise = new Promise(function (resolve, reject) {
          // CSS first (so the map doesn't flash unstyled)
          if (!document.querySelector('link[data-leaflet]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = LEAFLET_CSS;
            link.setAttribute('data-leaflet', '1');
            document.head.appendChild(link);
          }
          const script = document.createElement('script');
          script.src = LEAFLET_JS;
          script.async = true;
          script.onload = function () { resolve(); };
          script.onerror = function () { reject(); };
          document.head.appendChild(script);
        });
      }
      _leafletPromise.then(cb, function () {
        // Failed to load — leave the fallback gradient showing.
      });
    }

    // --- BUILD MAP -------------------------------------------------
    function buildMap(card, mapEl, lat, lng, zoom) {
      if (!window.L) return;
      const L = window.L;

      const map = L.map(mapEl, {
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
        doubleClickZoom: true,
        dragging: !reduceMotion,
        keyboard: false,
        tap: true
      }).setView([lat, lng], zoom);

      // OpenStreetMap tiles with a CSS dark-mode filter (see runtime
      // CSS at bottom of this file). OSM's own tile server is free
      // and volunteer-supported; no API key, no rate limit for
      // modest traffic. More reliable than CARTO's basemaps CDN,
      // which was rate-limiting anonymous requests.
      L.tileLayer(
        'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
          maxZoom: 19,
          minZoom: 3,
          attribution: '© OpenStreetMap',
          crossOrigin: true
        }
      ).addTo(map);

      // Custom pulsing marker (re-uses the same CSS as the fallback pin)
      const icon = L.divIcon({
        className: 'cs-location__pin',
        html:
          '<span class="cs-location__pin-pulse"></span>' +
          '<span class="cs-location__pin-ring"></span>' +
          '<span class="cs-location__pin-dot"></span>',
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
      L.marker([lat, lng], { icon: icon, interactive: false, keyboard: false }).addTo(map);

      // Re-add attribution in the bottom-right with compact styling
      L.control.attribution({ prefix: false, position: 'bottomright' })
        .addAttribution('© OpenStreetMap')
        .addTo(map);

      // --- Custom map controls: + / − / recenter, styled + grouped ---
      const Ctrl = L.Control.extend({
        options: { position: 'topright' },
        onAdd: function () {
          const c = L.DomUtil.create('div', 'cs-location__controls');
          c.innerHTML =
            '<a href="#" class="cs-location__ctrl" data-cs-ctrl="in" title="Přiblížit" aria-label="Přiblížit" role="button">' +
              '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
                '<path d="M8 3v10 M3 8h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
              '</svg>' +
            '</a>' +
            '<a href="#" class="cs-location__ctrl" data-cs-ctrl="out" title="Oddálit" aria-label="Oddálit" role="button">' +
              '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
                '<path d="M3 8h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
              '</svg>' +
            '</a>' +
            '<a href="#" class="cs-location__ctrl cs-location__ctrl--reset" data-cs-ctrl="reset" title="Zpět na původní pohled" aria-label="Zpět na původní pohled" role="button">' +
              '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
                '<circle cx="8" cy="8" r="1.5" fill="currentColor"/>' +
                '<circle cx="8" cy="8" r="4.5" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
                '<path d="M8 1.5v1.8 M8 12.7v1.8 M1.5 8h1.8 M12.7 8h1.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>' +
              '</svg>' +
            '</a>';

          // Don't let button clicks / scrolls reach the map
          L.DomEvent.disableClickPropagation(c);
          L.DomEvent.disableScrollPropagation(c);

          const onClick = function (action, e) {
            L.DomEvent.preventDefault(e);
            if (action === 'in')    map.zoomIn();
            if (action === 'out')   map.zoomOut();
            if (action === 'reset') map.flyTo([lat, lng], zoom, { duration: 0.6 });
          };

          c.querySelectorAll('[data-cs-ctrl]').forEach(function (btn) {
            L.DomEvent.on(btn, 'click', onClick.bind(null, btn.getAttribute('data-cs-ctrl')));
          });

          return c;
        }
      });
      new Ctrl().addTo(map);

      // Respect reduced motion — no pan animations
      if (reduceMotion) {
        map.scrollWheelZoom.disable();
        map.doubleClickZoom.disable();
      }

      // Mark ready after a few frames — the bento's reveal animation
      // may still be shifting the container, so we re-invalidate
      // multiple times to catch late layout changes (font loading,
      // grid resolution, image decoding, etc.).
      function markReady() { card.classList.add('is-map-ready'); }

      requestAnimationFrame(function () {
        try { map.invalidateSize(false); } catch (_) {}
        markReady();
      });
      // Catch any post-transition size shifts
      setTimeout(function () {
        try { map.invalidateSize(false); } catch (_) {}
      }, 250);
      setTimeout(function () {
        try { map.invalidateSize(false); } catch (_) {}
      }, 1000);

      // Invalidate when fonts finish loading (they can nudge layout)
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          try { map.invalidateSize(false); } catch (_) {}
        });
      }

      // Invalidate on resize + orientation
      let resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          try { map.invalidateSize(false); } catch (_) {}
        }, 200);
      });

      // ResizeObserver on the container itself — catches bento
      // reveal transitions that change the map's size.
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(function () {
          try { map.invalidateSize(false); } catch (_) {}
        });
        ro.observe(mapEl);
      }
    }
  })();

  // ==================================================================
  // 8. HERO PRODUCT CARD — client-side fallback.
  //
  //    Shopify's hero-section render cache has been serving an ancient
  //    version of the hero with legacy classes (cs-hero__title,
  //    cs-hero__subtitle, cs-hero__dossier, cs-hero__farmer). This
  //    cleanup rewrites the hero content to the current structure:
  //    meta → headline → product card. Uses real Palmor data.
  //
  //    Once Shopify's cache catches up, the server render will already
  //    include [data-cs-hero-product], and this falls through as a
  //    no-op.
  // ==================================================================
  (function heroProductCard() {
    const heroContent = document.querySelector('.cs-hero__content');
    if (!heroContent) return;

    // Palmor product data (default for this page).
    const palmor = {
      href:  '/products/palmor-castillo',
      image: 'https://cdn.shopify.com/s/files/1/0804/7018/0079/files/palmor_250.png?v=1775499123'
    };

    // If the server-rendered card is already present, we only need to
    // backfill the image when it's just showing the placeholder SVG
    // (happens when no product is picked in the theme editor).
    const existing = heroContent.querySelector('.cs-hero__product');
    if (existing) {
      const imgHost = existing.querySelector('.cs-hero__product-image');
      const hasRealImage = imgHost && imgHost.querySelector('img');
      if (imgHost && !hasRealImage) {
        // Swap placeholder → real <img>
        imgHost.innerHTML =
          '<img src="' + appendQuery(palmor.image, 'width=160') + '"' +
          ' srcset="' +
            appendQuery(palmor.image, 'width=120') + ' 120w, ' +
            appendQuery(palmor.image, 'width=160') + ' 160w, ' +
            appendQuery(palmor.image, 'width=240') + ' 240w"' +
          ' sizes="64px" alt="" width="64" height="64"' +
          ' loading="lazy" decoding="async">';
      }
      return;
    }

    // STEP 1 — nuke any LEGACY hero elements that the stale cache is
    // still serving (they clash visually with the new design).
    const LEGACY_SELECTORS = [
      '.cs-hero__eyebrow',
      '.cs-hero__title',
      '.cs-hero__subtitle',
      '.cs-hero__dossier',
      '.cs-hero__farmer',
      '.cs-hero__stamp',
      '.cs-hero__place'
    ];
    let legacyRemoved = 0;
    LEGACY_SELECTORS.forEach(function (sel) {
      heroContent.querySelectorAll(sel).forEach(function (n) {
        n.remove();
        legacyRemoved++;
      });
    });

    // STEP 2 — if the stale render lacked the new structure entirely,
    // bootstrap meta + headline so the hero isn't empty.
    if (legacyRemoved && !heroContent.querySelector('.cs-hero__headline')) {
      const meta = document.createElement('div');
      meta.className = 'cs-hero__meta';
      meta.innerHTML =
        '<span>Evelina</span>' +
        '<span class="cs-hero__meta-dot" aria-hidden="true"></span>' +
        '<span>Sklizeň 2026</span>';

      const headline = document.createElement('h1');
      headline.className = 'cs-hero__headline';
      headline.innerHTML =
        '<span class="cs-hero__headline-line">Tohle je příběh kávy,</span>' +
        '<span class="cs-hero__headline-line">kterou právě držíte.</span>';

      heroContent.prepend(headline);
      heroContent.prepend(meta);
    }

    // STEP 3 — hide any lingering signature since the product card
    // takes over that role visually.
    heroContent.querySelectorAll('.cs-hero__signature').forEach(function (n) {
      n.style.display = 'none';
    });

    // STEP 4 — build and insert the product card.
    const data = {
      href:     palmor.href,
      image:    palmor.image,
      eyebrow:  'Vaše káva',
      name:     'Palmor Castillo',
      meta:     'Kolumbie · Washed · 250 g'
    };

    const card = document.createElement('a');
    card.className = 'cs-hero__product';
    card.setAttribute('data-cs-hero-product', '');
    card.setAttribute('href', data.href);
    card.setAttribute('aria-label', data.name);
    card.innerHTML =
      '<div class="cs-hero__product-image" aria-hidden="true">' +
        '<img src="' + appendQuery(data.image, 'width=160') + '"' +
        ' srcset="' +
          appendQuery(data.image, 'width=120') + ' 120w, ' +
          appendQuery(data.image, 'width=160') + ' 160w, ' +
          appendQuery(data.image, 'width=240') + ' 240w"' +
        ' sizes="64px" alt="" width="64" height="64"' +
        ' loading="lazy" decoding="async">' +
      '</div>' +
      '<div class="cs-hero__product-info">' +
        '<div class="cs-hero__product-eyebrow">' + data.eyebrow + '</div>' +
        '<div class="cs-hero__product-name">'    + data.name    + '</div>' +
        '<div class="cs-hero__product-meta">'    + data.meta    + '</div>' +
      '</div>' +
      '<span class="cs-hero__product-arrow" aria-hidden="true">' +
        '<svg viewBox="0 0 16 16" width="11" height="11">' +
          '<path d="M4 12 L12 4 M6 4 L12 4 L12 10" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
      '</span>';

    heroContent.appendChild(card);

    // Safely append a query-string param regardless of whether the
    // URL already has one (Shopify CDN URLs usually carry ?v=…).
    function appendQuery(url, param) {
      return url + (url.indexOf('?') > -1 ? '&' : '?') + param;
    }
  })();

  // ==================================================================
  // CS_RUNTIME_CSS — essential fallback styles. Kept in ONE string so
  // it's trivial for the minifier to inline. Covers:
  //   • Hero product card
  //   • Location card (map + pin + stats + strip)
  //   • Bento grid (responsive)
  // Deliberately does NOT redefine existing classes like .cs-hero,
  // .cs-chapter — those are already styled by the theme CSS.
  // ==================================================================
  const CS_RUNTIME_CSS = [
    /* ==================================================================
       HERO — strip any stale CSS classes the old server snippet inlined,
       then add only what the new 3-beat hero needs.
       ================================================================== */
    '.cs-hero__content .cs-hero__title,.cs-hero__content .cs-hero__subtitle,.cs-hero__content .cs-hero__dossier,.cs-hero__content .cs-hero__farmer,.cs-hero__content .cs-hero__eyebrow,.cs-hero__content .cs-hero__stamp,.cs-hero__content .cs-hero__place,.cs-hero__content .cs-hero__signature{display:none!important}',
    '.cs-hero__meta{display:inline-flex;align-items:center;gap:12px;font-family:"JetBrains Mono",ui-monospace,monospace;font-size:10px;letter-spacing:.3em;text-transform:uppercase;color:rgba(255,255,255,.55);margin:0 0 32px 0}',
    '.cs-hero__meta-dot{width:3px;height:3px;background:rgba(255,255,255,.3);border-radius:50%;flex-shrink:0}',
    '.cs-hero__headline{margin:0 0 36px 0;font-family:"Fraunces",Georgia,serif;font-weight:300;font-size:clamp(36px,8.2vw,60px);line-height:1.02;letter-spacing:-.025em;color:#f4ede1;text-wrap:balance}',
    '.cs-hero__headline-line{display:block}',
    '.cs-hero__headline-line+.cs-hero__headline-line{color:rgba(255,255,255,.54)}',

    /* ---- Hero product card — matches the site's native product cards.
       Creamy background (from product's `coffee.card_color` metafield),
       dark text, larger image, Inter typography, soft shadow. Sits on
       the dark hero like the main-page product grid cards do. */
    '.cs-hero__product{--cs-product-bg:#EADFCE;--cs-product-fg:#1f1e1b;display:inline-flex;align-items:center;gap:18px;padding:16px 24px 16px 16px;margin:0 0 48px 0;max-width:100%;background:var(--cs-product-bg);color:var(--cs-product-fg);border:none;border-radius:14px;text-decoration:none;box-shadow:0 1px 2px rgba(0,0,0,.18),0 8px 24px rgba(0,0,0,.12);opacity:0;transform:translateY(12px);animation:cs-hero-beat .9s ease .80s forwards;transition:transform .3s ease,box-shadow .3s ease}',
    '@media(hover:hover){a.cs-hero__product:hover{transform:translateY(-2px);box-shadow:0 2px 4px rgba(0,0,0,.2),0 18px 32px rgba(0,0,0,.22)}a.cs-hero__product:hover .cs-hero__product-arrow{transform:translate(3px,-3px);color:var(--cs-product-fg)}}',
    '.cs-hero__product-image{position:relative;width:72px;height:72px;border-radius:8px;overflow:hidden;flex-shrink:0;background:rgba(0,0,0,.04)}',
    '.cs-hero__product-image img{width:100%;height:100%;object-fit:cover;display:block}',
    '.cs-hero__product-image-placeholder{display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:rgba(0,0,0,.3)}',
    '.cs-hero__product-info{display:flex;flex-direction:column;gap:4px;min-width:0;flex:1 1 auto}',
    '.cs-hero__product-eyebrow{font-family:"Inter",system-ui,sans-serif;font-size:10px;font-weight:500;letter-spacing:.16em;text-transform:uppercase;color:rgba(31,30,27,.55);line-height:1}',
    '.cs-hero__product-name{font-family:"Inter",system-ui,sans-serif;font-weight:500;font-style:normal;font-size:clamp(18px,3vw,22px);line-height:1.2;letter-spacing:-.01em;color:var(--cs-product-fg);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '.cs-hero__product-meta{font-family:"Inter",system-ui,sans-serif;font-size:12.5px;font-weight:400;letter-spacing:0;color:rgba(31,30,27,.62);line-height:1.35;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '.cs-hero__product-arrow{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;flex-shrink:0;color:rgba(31,30,27,.45);margin-left:auto;padding-left:8px;transition:color .3s ease,transform .3s ease}',
    '@media(max-width:600px){.cs-hero__product{padding:14px 20px 14px 14px;gap:14px;border-radius:12px}.cs-hero__product-image{width:60px;height:60px;border-radius:7px}.cs-hero__product-name{font-size:17px}.cs-hero__product-meta{font-size:12px}}',

    /* ==================================================================
       BENTO — editorial three-part composition, no tiles, no chrome.
         Row 1 (desktop grid): Map | Text
         Row 2 (thin rule):    Inline meta line (4 fields + link)
         Row 3 (full bleed):   Video
       ================================================================== */
    '.cs-bento{position:relative;max-width:1080px;margin:clamp(40px,8vw,96px) auto;padding:0 clamp(20px,5vw,48px);display:flex;flex-direction:column;gap:clamp(32px,5vw,56px);opacity:0;transform:translateY(16px);transition:opacity .9s ease,transform .9s ease}',
    '.cs-bento.is-visible{opacity:1;transform:translateY(0)}',

    /* ---- Row 1: map | text ---- */
    '.cs-bento__grid{display:grid;grid-template-columns:1fr;gap:clamp(28px,4vw,40px);align-items:center}',
    '@media(min-width:900px){.cs-bento__grid{grid-template-columns:1.1fr 1fr;gap:clamp(44px,5vw,72px)}}',

    '.cs-bento__map-wrap{position:relative;aspect-ratio:4/3;overflow:hidden;background:#1a1a18;border-radius:4px}',
    '@media(min-width:900px){.cs-bento__map-wrap{aspect-ratio:5/4}}',

    '.cs-bento__text{display:flex;flex-direction:column;gap:clamp(16px,2vw,22px);max-width:46ch}',
    '.cs-bento__meta{display:flex;align-items:center;gap:12px;font-family:"JetBrains Mono",ui-monospace,monospace;font-size:10px;letter-spacing:.3em;text-transform:uppercase;line-height:1}',
    '.cs-bento__num{color:#c4a37c;font-weight:500}',
    '.cs-bento__meta-dot{display:inline-block;width:14px;height:1px;background:rgba(255,255,255,.28)}',
    '.cs-bento__kicker{color:rgba(255,255,255,.68)}',
    '.cs-bento__heading{margin:0;font-family:"Fraunces",Georgia,serif;font-weight:300;font-size:clamp(28px,5vw,44px);line-height:1.02;letter-spacing:-.025em;color:#f4ede1;text-wrap:balance}',
    '.cs-bento__body{font-family:"Inter",system-ui,sans-serif;font-size:clamp(14px,2.2vw,15.5px);line-height:1.75;color:rgba(255,255,255,.66)}',
    '.cs-bento__body p{margin:0 0 1em 0}',
    '.cs-bento__body p:last-child{margin-bottom:0}',

    /* ---- Row 2: inline meta line (fields + map link) ---- */
    '.cs-bento__line{display:flex;flex-wrap:wrap;gap:clamp(20px,3vw,36px);padding:clamp(18px,2.6vw,24px) 0;border-top:1px solid rgba(255,255,255,.09);border-bottom:1px solid rgba(255,255,255,.09);align-items:baseline;justify-content:space-between}',
    '.cs-bento__line-group{display:flex;flex-wrap:wrap;gap:clamp(20px,3vw,36px);align-items:baseline;min-width:0}',
    '.cs-bento__field{display:inline-flex;flex-direction:column;gap:5px;min-width:0}',
    '.cs-bento__field-label{font-family:"JetBrains Mono",ui-monospace,monospace;font-size:9px;letter-spacing:.26em;text-transform:uppercase;color:rgba(255,255,255,.4);line-height:1}',
    '.cs-bento__field-value{font-family:"Fraunces",Georgia,serif;font-weight:300;font-size:clamp(15px,2.4vw,17px);color:#f4ede1;font-variant-numeric:tabular-nums;letter-spacing:-.005em;line-height:1.15;min-height:1.15em;display:inline-flex;align-items:center;gap:8px}',
    '.cs-bento__line-link{display:inline-flex;align-items:center;gap:8px;font-family:"JetBrains Mono",ui-monospace,monospace;font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:#c4a37c;text-decoration:none;border-bottom:1px solid rgba(196,163,124,.3);padding-bottom:3px;transition:color .25s ease,border-color .25s ease}',
    '.cs-bento__line-link svg{transition:transform .25s ease}',
    '@media(hover:hover){.cs-bento__line-link:hover{color:#d9b78a;border-color:rgba(196,163,124,.7)}.cs-bento__line-link:hover svg{transform:translate(2px,-2px)}}',

    /* Weather line inside field-value */
    '.cs-bento__field-value .cs-location__weather-icon{width:16px;height:16px;color:rgba(255,255,255,.72);flex-shrink:0}',
    '.cs-bento__field-value .cs-location__weather-temp{font-variant-numeric:tabular-nums}',
    '.cs-bento__field-value .cs-location__weather-desc{font-family:"JetBrains Mono",ui-monospace,monospace;font-size:10px;font-weight:400;letter-spacing:.1em;text-transform:lowercase;color:rgba(255,255,255,.48)}',

    /* ---- Row 3: full-bleed video ---- */
    '.cs-bento__video{aspect-ratio:16/9;overflow:hidden;background:#141410;border-radius:4px}',
    '.cs-bento__video .cs-chapter__media,.cs-bento__video .cs-chapter__image,.cs-bento__video .cs-chapter__video,.cs-bento__video video,.cs-bento__video img{width:100%;height:100%;display:block;object-fit:cover;margin:0;border-radius:0}',
    '.cs-bento__video .cs-chapter__image--placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-family:"JetBrains Mono",ui-monospace,monospace;font-size:10px;letter-spacing:.24em;text-transform:uppercase;color:rgba(255,255,255,.3)}',

    /* ==================================================================
       LOCATION CARD — in-bento variant (fills map-wrap)
       ================================================================== */
    '.cs-location--in-bento{display:block;margin:0;background:transparent;border:none;border-radius:0;opacity:1;transform:none;height:100%;position:absolute;inset:0}',
    '.cs-location--in-bento .cs-location__map{aspect-ratio:auto;height:100%;border:none;position:relative;background:#1a1a18;overflow:hidden;isolation:isolate}',
    '.cs-location--in-bento .cs-location__body{display:none}',
    '.cs-location__map-fallback{position:absolute;inset:0;z-index:0;transition:opacity .6s ease}',
    '.cs-location.is-map-ready .cs-location__map-fallback{opacity:0;pointer-events:none}',
    '.cs-location__map-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.035) 1px,transparent 1px);background-size:28px 28px;background-position:center;-webkit-mask-image:radial-gradient(ellipse at center,black 30%,transparent 78%);mask-image:radial-gradient(ellipse at center,black 30%,transparent 78%)}',
    '.cs-location__map-glow{position:absolute;inset:0;background:radial-gradient(circle at 50% 50%,rgba(196,163,124,.12) 0%,rgba(196,163,124,.04) 22%,transparent 48%)}',
    '.cs-location__map-inner{position:absolute;inset:0;z-index:1;opacity:0;transition:opacity .8s ease}',
    '.cs-location.is-map-ready .cs-location__map-inner{opacity:1}',
    '.cs-location__map-vignette{position:absolute;inset:0;z-index:2;pointer-events:none;background:radial-gradient(ellipse at center,transparent 55%,rgba(20,20,18,.4) 100%)}',

    /* Leaflet dark-mode: invert + hue-rotate to make OSM look premium dark */
    '.cs-location .leaflet-container{background:#141410;font-family:"Inter",system-ui,sans-serif}',
    '.cs-location .leaflet-tile{filter:invert(1) hue-rotate(180deg) saturate(.4) brightness(.78) contrast(1.06)}',
    '.cs-location .leaflet-control-zoom{display:none!important}',
    '.cs-location .leaflet-control-attribution{background:rgba(10,9,7,.55)!important;color:rgba(255,255,255,.38)!important;font-family:"JetBrains Mono",ui-monospace,monospace!important;font-size:9px!important;padding:3px 8px!important;border-radius:2px 0 0 0}',
    '.cs-location .leaflet-control-attribution a{color:rgba(255,255,255,.52)!important}',

    /* ---- Google Maps 3D (Photorealistic Tiles) ---- */
    'gmp-map-3d{display:block;width:100%;height:100%;--gmp-map-3d-theme:dark}',
    '.cs-location__map:has(gmp-map-3d){background:#0c0c0a}',
    /* 3D variant of our custom controls — position absolute inside the
       map-wrap (since Map3DElement doesn\'t use Leaflet\'s control panes) */
    '.cs-location__controls--3d{position:absolute;top:12px;right:12px;z-index:5}',

    /* ---- Custom branded map badge (top-left corner of map) ---- */
    '.cs-map-badge{position:absolute;bottom:14px;left:14px;z-index:5;max-width:240px;min-width:170px;padding:12px 16px 14px;background:rgba(234,223,206,.92);backdrop-filter:blur(14px) saturate(1.15);-webkit-backdrop-filter:blur(14px) saturate(1.15);border:1px solid rgba(196,163,124,.4);border-radius:10px;box-shadow:0 2px 4px rgba(0,0,0,.2),0 12px 24px rgba(0,0,0,.15);color:#1f1e1b;opacity:0;transform:translateY(10px);transition:opacity .8s cubic-bezier(.16,.8,.42,1),transform .8s cubic-bezier(.16,.8,.42,1);pointer-events:auto}',
    '.cs-map-badge.is-visible{opacity:1;transform:translateY(0)}',
    '.cs-map-badge.is-leaving{opacity:0;transform:translateY(10px);transition:opacity .6s ease-out,transform .6s ease-out}',
    '.cs-map-badge.is-hidden{opacity:0;transform:translateY(10px);transition:none}',
    '.cs-map-badge__header{display:flex;align-items:center;gap:10px}',
    '.cs-map-badge__mark{display:flex;align-items:center;justify-content:center;width:20px;height:20px;color:#c4a37c;flex-shrink:0}',
    '.cs-map-badge__titles{display:flex;flex-direction:column;gap:2px;min-width:0}',
    '.cs-map-badge__eyebrow{font-family:"JetBrains Mono",ui-monospace,monospace;font-size:9px;font-weight:500;letter-spacing:.28em;text-transform:uppercase;color:rgba(31,30,27,.52);line-height:1}',
    '.cs-map-badge__name{font-family:"Fraunces",Georgia,serif;font-weight:400;font-style:italic;font-size:17px;letter-spacing:-.01em;color:#1f1e1b;line-height:1.1}',
    '.cs-map-badge__rule{height:1px;background:linear-gradient(90deg,rgba(196,163,124,.55),rgba(196,163,124,0));margin:11px 0 10px}',
    '.cs-map-badge__stats{margin:0;display:flex;flex-direction:column;gap:8px}',
    '.cs-map-badge__stat{display:flex;flex-direction:column;gap:2px}',
    '.cs-map-badge__stat-label{font-family:"JetBrains Mono",ui-monospace,monospace;font-size:8.5px;font-weight:500;letter-spacing:.24em;text-transform:uppercase;color:rgba(31,30,27,.5);line-height:1;margin:0}',
    '.cs-map-badge__stat-value{font-family:"Inter",system-ui,sans-serif;font-size:12px;font-weight:400;color:rgba(31,30,27,.85);line-height:1.25;font-variant-numeric:tabular-nums;margin:0}',
    '@media(max-width:600px){.cs-map-badge{bottom:10px;left:10px;max-width:200px;min-width:150px;padding:10px 14px 12px}.cs-map-badge__name{font-size:15px}.cs-map-badge__rule{margin:9px 0 8px}.cs-map-badge__stat-value{font-size:11.5px}}',
    '@media(prefers-reduced-motion:reduce){.cs-map-badge{transition:none;opacity:1;transform:none}.cs-map-badge.is-hidden,.cs-map-badge.is-leaving{opacity:0}}',

    /* ---- Custom controls: zoom in / zoom out / recenter ---- */
    '.cs-location__controls{display:flex;flex-direction:column;border-radius:4px;overflow:hidden;background:rgba(16,15,13,.78);backdrop-filter:blur(14px) saturate(1.1);-webkit-backdrop-filter:blur(14px) saturate(1.1);border:1px solid rgba(255,255,255,.09);box-shadow:0 6px 18px rgba(0,0,0,.45);margin:12px!important}',
    '.cs-location__ctrl{display:flex;align-items:center;justify-content:center;width:36px;height:36px;color:rgba(255,255,255,.68);text-decoration:none;transition:color .2s ease,background .2s ease;cursor:pointer}',
    '.cs-location__ctrl+.cs-location__ctrl{border-top:1px solid rgba(255,255,255,.08)}',
    '.cs-location__ctrl--reset{border-top:1px solid rgba(255,255,255,.14)!important}',
    '.cs-location__ctrl:hover{background:rgba(255,255,255,.05);color:#fff}',
    '.cs-location__ctrl:focus-visible{outline:1px solid rgba(196,163,124,.55);outline-offset:-1px;background:rgba(255,255,255,.05);color:#fff}',
    '.cs-location__ctrl:active{background:rgba(255,255,255,.09)}',
    '.cs-location__ctrl svg{display:block;opacity:.9}',
    '@media(max-width:720px){.cs-location__ctrl{width:40px;height:40px}}',

    /* ---- Pin — subtle, calm (no ring pulse, no glow ring) ---- */
    '.cs-location__pin,.cs-location__pin--fallback{position:absolute;top:50%;left:50%;width:16px;height:16px;margin:-8px 0 0 -8px;z-index:3;pointer-events:none}',
    '.cs-location.is-map-ready .cs-location__pin--fallback{opacity:0;transition:opacity .4s ease}',
    '.cs-location__pin-dot{position:absolute;top:50%;left:50%;width:7px;height:7px;margin:-3.5px 0 0 -3.5px;background:#c4a37c;border-radius:50%;box-shadow:0 0 0 2px rgba(20,20,18,.95)}',
    '.cs-location__pin-ring{position:absolute;inset:0;border:1px solid rgba(196,163,124,.35);border-radius:50%;animation:cs-pin-ring 3.2s infinite ease-out}',
    '@keyframes cs-pin-ring{0%{transform:scale(.6);opacity:0}20%{opacity:.5}100%{transform:scale(2.2);opacity:0}}',
    '.cs-location__pin-pulse{display:none}',

    /* ---- Reduced motion ---- */
    '@media(prefers-reduced-motion:reduce){.cs-bento{transition:none;opacity:1;transform:none}.cs-location__pin-ring{animation:none}}'
  ].join('\n');

  // Now that CS_RUNTIME_CSS is initialised, actually inject it.
  injectFallbackCSS();
})();
